// InvoiceManagement.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
    Autocomplete,
    Box,
    Button,
    Card,
    CardContent,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    FormLabel,
    FormHelperText,
    MenuItem,
    Radio,
    RadioGroup,
    TextField,
    Typography,
    Checkbox,
    List,
    ListItem,
    ListItemText,
    Snackbar,
    Alert,
    Container,
    Stack,
    Chip,
    ToggleButton,
    ToggleButtonGroup,
    CircularProgress,
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";

import {
    getCustomers,
    getArticles,
    getShippingCosts,
    getProfil,
    getProfilLogo,
    getInvoiceMonths,
    getInvoicesForMonth,
    getOffers,
    getWorkOrders,
    createInvoice,
    sendInvoiceEmail,
    cancelInvoice,
    downloadInvoicePdf,
    downloadInvoiceXRechnung,
    getInvoiceNextNumber,
    updateInvoicePaid,
    updateInvoiceSent,
    updateInvoiceWorkOrder,
    createInvoicePaymentLink,
    previewInvoicePdf,
} from "../../api";
import { logError, logInfo } from "../../logging/loggingService";
import AppDatePicker from "../shared/AppDatePicker";
import {
    buildCustomerOptions,
    CUSTOMER_SELECT_EMPTY_HELPER,
    CUSTOMER_SELECT_LABEL,
    CUSTOMER_SELECT_LOADING,
    CUSTOMER_SELECT_NO_OPTIONS,
    CUSTOMER_SELECT_PLACEHOLDER,
    getCustomerOptionLabel,
    isCustomerOptionEqualToValue,
    resolveCustomerOption,
} from "../shared/customerSelectUtils";
import MultilineTextField from "../shared/MultilineTextField";
import InvoicePreviewCard from "./invoice-management/InvoicePreviewCard";
import InvoiceTypeStep from "./invoice-management/InvoiceTypeStep";
import InvoiceList from "./invoice-management/InvoiceList";
import {
    buildMonthKey,
    buildShippingCostArticle,
    extractInvoices,
    fmtEUR,
    fmtCurrency,
    getDefaultInvoiceGroupState,
    getInvoiceOutstandingAmount,
    getInvoicePaidAmount,
    invoiceTypeChipColor,
    invoiceTypeLabels,
    invoiceGroupStateKey,
    normalizeInvoiceArticles,
    normalizeInvoiceMonths,
    readInvoiceGroupState,
    roundMoney,
    toNumberSafe,
    wizardSteps,
} from "./invoice-management/invoiceManagementUtils";
import { ensureFreshToken, isKeycloakMode } from "../../auth/keycloakClient";
import { suppressAuthEscalationFor } from "../../auth/authEscalationGuard";

const POST_SEND_AUTH_ESCALATION_SUPPRESSION_MS = 20000;


export default function InvoiceManagement({ mode = "invoice" }) {
    const isOffer = mode === "offer";
    const documentLabel = isOffer ? "Angebot" : "Rechnung";
    const documentLabelPlural = isOffer ? "Angebote" : "Rechnungen";
    const documentNumberLabel = isOffer ? "Angebotsnummer" : "Rechnungsnummer";
    const documentDateLabel = isOffer ? "Angebotsdatum" : "Rechnungsdatum";
    const documentDueLabel = isOffer ? "Gueltig bis" : "Zahlungsziel";
    const documentSubtitle = isOffer
        ? "Erstelle und verwalte Angebote - alles an einem Ort."
        : "Erstelle, verwalte und markiere Zahlungen - alles an einem Ort.";
    const createDocumentLabel = isOffer ? "Neues Angebot" : "Neue Rechnung";
    const nextNumberErrorMessage = isOffer
        ? "Fehler beim Laden der naechsten Angebotsnummer"
        : "Fehler beim Laden der nächsten Rechnungsnummer";

    const PAYMENT_TERM_OPTIONS = [7, 14, 30];

    const [openDialog, setOpenDialog] = useState(false);
    const [customers, setCustomers] = useState([]);
    const [articles, setArticles] = useState([]);
    const [shippingCosts, setShippingCosts] = useState([]);
    const [workOrders, setWorkOrders] = useState([]);
    const [profil, setProfil] = useState(null);
    const location = useLocation();
    const navigate = useNavigate();
    const invoicePrefill = location?.state?.invoicePrefill;
    const invoicePrefillRef = useRef(false);

    const [selectedCustomerId, setSelectedCustomerId] = useState("");
    const [selectedWorkOrderId, setSelectedWorkOrderId] = useState("");
    const [selectedArticleId, setSelectedArticleId] = useState("");
    const [selectedShippingCostId, setSelectedShippingCostId] = useState("");
    const [invoiceArticles, setInvoiceArticles] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [selectedLocation, setSelectedLocation] = useState("");
    const [selectedAmount, setSelectedAmount] = useState("1");
    const [articleDiscountPercent, setArticleDiscountPercent] = useState(0);
    const [articleDiscountReason, setArticleDiscountReason] = useState("");
    const [useArticleDiscount, setArticleDiscount] = useState(false);

    const [isFreeText, setIsFreeText] = useState(false);
    const [freeTextName, setFreeTextName] = useState("");
    const [freeTextPrice, setFreeTextPrice] = useState("");
    const [freeTextTaxRate, setFreeTextTaxRate] = useState(19);
    const [freeTextUnit, setFreeTextUnit] = useState("Stk");
    const [invoiceFreeText, setInvoiceFreeText] = useState("");

    const [activeStep, setActiveStep] = useState(0);
    const [invoiceType, setInvoiceType] = useState(isOffer ? "OFFER" : "STANDARD");
    const [parentInvoiceId, setParentInvoiceId] = useState("");
    const [offerParentId, setOfferParentId] = useState("");
    const [partPercent, setPartPercent] = useState("");
    const [baseAmountOverride, setBaseAmountOverride] = useState("");
    const [parentInvoice, setParentInvoice] = useState(null);

    const parseNumberInput = (value) => {
        if (value === "" || value === null || value === undefined) return null;
        const normalized = value.toString().replace(",", ".");
        const parsed = parseFloat(normalized);
        return Number.isFinite(parsed) ? parsed : null;
    };

    const parsePositiveDecimalInput = (raw) => {
        const s = String(raw ?? "").trim();
        if (!s) {
            return { value: null, error: null };
        }

        // Strict parsing: prevent accidental coercion like "abc" -> NaN (later defaulting to 1).
        // Accept "1", "1,5", "1.5" (German comma decimals).
        if (!/^[0-9]+([.,][0-9]+)?$/.test(s)) {
            return { value: null, error: "invalid" };
        }

        const n = parseFloat(s.replace(",", "."));
        if (!Number.isFinite(n)) {
            return { value: null, error: "invalid" };
        }

        if (n < 0.01) {
            return { value: n, error: "too_small" };
        }

        return { value: n, error: null };
    };

    const [totalDiscountType, setTotalDiscountType] = useState("none"); // "none" | "percent" | "fixed"
    const [totalDiscountValue, setTotalDiscountValue] = useState(0);

    const [invoiceDate, setInvoiceDate] = useState(() => new Date().toISOString().slice(0, 10));
    const [paymentTermDays, setPaymentTermDays] = useState(7); // Default to 7 days
    const [isReverseCharge, setIsReverseCharge] = useState(false); // New state for Reverse Charge
    const [dueDate, setDueDate] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() + 7); // Default to +7 days
        return d.toISOString().slice(0, 10);
    });
    const [performanceDate, setPerformanceDate] = useState(() =>
        new Date().toISOString().slice(0, 10)
    );
    const [createXRechnung, setCreateXRechnung] = useState(!isOffer);
    const [sendInvoiceEmailEnabled, setSendInvoiceEmailEnabled] = useState(false);
    const [isPaid, setIsPaid] = useState(false);

    const hasCustomerVatId = useMemo(() => {
        return Boolean(selectedCustomer?.taxData?.ustId || selectedCustomer?.taxData?.vatNumber);
    }, [selectedCustomer]);

    const [logoUrl, setLogoUrl] = useState(null);
    const [existingInvoices, setExistingInvoices] = useState([]);
    const [invoiceMonthIndex, setInvoiceMonthIndex] = useState([]);
    const [invoiceMonthIndexLoaded, setInvoiceMonthIndexLoaded] = useState(false);
    const [invoiceMonthData, setInvoiceMonthData] = useState({});
    const [loadingInvoiceMonths, setLoadingInvoiceMonths] = useState(() => new Set());
    const [loadedInvoiceMonths, setLoadedInvoiceMonths] = useState(() => new Set());
    const [invoiceNextNumber, setInvoiceNextNumber] = useState("");

    const [paymentDialogOpen, setPaymentDialogOpenState] = useState(false);
    const [paymentInvoice, setPaymentInvoice] = useState(null);
    const [paymentDate, setPaymentDate] = useState(() => new Date().toISOString().slice(0, 10));
    const [partialPaymentDialogOpen, setPartialPaymentDialogOpen] = useState(false);
    const [partialPaymentInvoice, setPartialPaymentInvoice] = useState(null);
    const [partialPaymentAmount, setPartialPaymentAmount] = useState("");
    const [partialPaymentDate, setPartialPaymentDate] = useState(() =>
        new Date().toISOString().slice(0, 10)
    );
    const [workOrderDialogOpen, setWorkOrderDialogOpen] = useState(false);
    const [workOrderInvoice, setWorkOrderInvoice] = useState(null);
    const [workOrderSelection, setWorkOrderSelection] = useState("");
    const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
    const [cancelInvoiceTarget, setCancelInvoiceTarget] = useState(null);
    const [cancelPending, setCancelPending] = useState(false);

    const [payLinkFilter, setPayLinkFilter] = useState("ALL");
    const [expandedInvoiceGroups, setExpandedInvoiceGroups] = useState(() => {
        const stored = readInvoiceGroupState();
        return stored ?? getDefaultInvoiceGroupState();
    });

    const [loading, setLoading] = useState(true);
    const [snack, setSnack] = useState({ open: false, msg: "", sev: "success" });
    const [fieldErrors, setFieldErrors] = useState({});
    const [articleErrors, setArticleErrors] = useState({});
    const currentMonthRef = useRef(null);
    const postSendRefreshTimerRef = useRef(null);

    if (!currentMonthRef.current) {
        const now = new Date();
        currentMonthRef.current = { year: now.getFullYear(), month: now.getMonth() };
    }

    useEffect(() => {
        if (!parentInvoiceId) {
            setParentInvoice(null);
            if (invoiceType !== "STANDARD") {
                setBaseAmountOverride("");
            }
            return;
        }
        const found = existingInvoices.find((inv) => inv.id === parentInvoiceId);
        setParentInvoice(found || null);
        if (found) {
            const base = found.baseAmount ?? found.totalGross;
            if (base !== undefined && base !== null) {
                setBaseAmountOverride(Number(base).toFixed(2));
            }
        }
    }, [parentInvoiceId, existingInvoices, invoiceType]);

    useEffect(() => {
        if (isOffer) return;
        if (invoiceType === "STANDARD") {
            setParentInvoiceId("");
            setParentInvoice(null);
            setPartPercent("");
            setBaseAmountOverride("");
        }
    }, [invoiceType, isOffer]);

    useEffect(() => {
        if (isOffer) return;
        const merged = Object.values(invoiceMonthData).flat();
        setExistingInvoices(merged);
    }, [invoiceMonthData, isOffer]);

    const parentMetrics = useMemo(() => {
        if (parentInvoice) {
            const base = parentInvoice.baseAmount ?? parentInvoice.totalGross ?? null;
            const remaining = parentInvoice.remainingAmount ?? null;
            return {
                base,
                remaining,
                consumed: base != null && remaining != null ? base - remaining : null,
            };
        }
        const override = parseNumberInput(baseAmountOverride);
        return { base: override, remaining: null, consumed: null };
    }, [parentInvoice, baseAmountOverride]);

    const projectedPartAmount = useMemo(() => {
        const base = parentMetrics.base;
        const pct = parseNumberInput(partPercent);
        if (base == null || pct == null) return null;
        return (base * pct) / 100;
    }, [parentMetrics.base, partPercent]);

    /* ===== Daten laden ===== */
    useEffect(() => {
        let mounted = true;
        let createdLogoUrl = null;

        const load = async () => {
            try {
                setLoading(true);
                const [cust, art, shipping, prof, orders] = await Promise.allSettled([
                    getCustomers(),
                    getArticles(),
                    getShippingCosts(),
                    getProfil(),
                    getWorkOrders(),
                ]);

                if (!mounted) return;

                if (cust.status === "fulfilled") setCustomers(cust.value.data || []);
                else showError("Fehler beim Laden der Kunden", cust.reason);

                if (art.status === "fulfilled") setArticles(art.value.data || []);
                else showError("Fehler beim Laden der Artikel", art.reason);

                if (shipping.status === "fulfilled") setShippingCosts(shipping.value.data || []);
                else showError("Fehler beim Laden der Versandkosten", shipping.reason);

                if (prof.status === "fulfilled") setProfil(prof.value.data || null);
                else showError("Fehler beim Laden des Profils", prof.reason);

                if (orders.status === "fulfilled") setWorkOrders(orders.value.data || []);
                else showError("Fehler beim Laden der Auftraege", orders.reason);

                await refreshInvoices();
            } finally {
                if (mounted) setLoading(false);
            }
        };

        load();

        getProfilLogo()
            .then((res) => {
                if (!mounted || !res?.data || res.status >= 400) return;
                const url = URL.createObjectURL(res.data);
                createdLogoUrl = url;
                setLogoUrl(url);
            })
            .catch(() => { /* kein Logo ist ok */ });
        generateInvoiceNumber();

        return () => {
            mounted = false;
            if (createdLogoUrl) {
                try { URL.revokeObjectURL(createdLogoUrl); } catch {}
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const ensureInvoiceMonthIndex = (year, monthKey) => {
        const yearNum = Number(year);
        const monthNum = Number(monthKey);
        if (!Number.isFinite(yearNum) || !Number.isFinite(monthNum)) return;
        setInvoiceMonthIndex((prev) => {
            const exists = prev.some((entry) => entry.year === yearNum && entry.month === monthNum);
            if (exists) return prev;
            return [...prev, { year: yearNum, month: monthNum }];
        });
    };

    const loadInvoiceMonthIndex = async (options = {}) => {
        if (isOffer) return [];
        if (invoiceMonthIndexLoaded && !options.force) return invoiceMonthIndex;
        try {
            const res = await getInvoiceMonths({ suppressAuthError: options.suppressAuthError });
            const normalized = normalizeInvoiceMonths(res?.data);
            setInvoiceMonthIndex(normalized);
            return normalized;
        } catch (err) {
            if (options.suppressAuthError && isAuthError(err)) {
                setInvoiceMonthIndex([]);
                throw err;
            }
            showError("Fehler beim Laden der Rechnungsmonate", err);
            setInvoiceMonthIndex([]);
            return [];
        } finally {
            setInvoiceMonthIndexLoaded(true);
        }
    };

    const loadInvoicesForMonthKey = async (year, monthKey, options = {}) => {
        if (isOffer) return [];
        const yearNum = Number(year);
        const monthNum = Number(monthKey);
        if (!Number.isFinite(yearNum) || !Number.isFinite(monthNum)) return [];
        const key = buildMonthKey(String(yearNum), String(monthNum));
        if (loadingInvoiceMonths.has(key)) return [];
        if (!options.force && loadedInvoiceMonths.has(key)) return invoiceMonthData[key] || [];
        setLoadingInvoiceMonths((prev) => {
            const next = new Set(prev);
            next.add(key);
            return next;
        });
        try {
            const res = await getInvoicesForMonth(yearNum, monthNum, {
                suppressAuthError: options.suppressAuthError,
            });
            const invoices = extractInvoices(res);
            setInvoiceMonthData((prev) => ({ ...prev, [key]: invoices }));
            setLoadedInvoiceMonths((prev) => {
                const next = new Set(prev);
                next.add(key);
                return next;
            });
            ensureInvoiceMonthIndex(yearNum, monthNum);
            return invoices;
        } catch (err) {
            if (options.suppressAuthError && isAuthError(err)) {
                throw err;
            }
            showError(`Fehler beim Laden der ${documentLabelPlural}`, err);
            return [];
        } finally {
            setLoadingInvoiceMonths((prev) => {
                const next = new Set(prev);
                next.delete(key);
                return next;
            });
        }
    };

    const refreshInvoices = async (options = {}) => {
        if (isOffer) {
            return getOffers({ suppressAuthError: options.suppressAuthError })
                .then((res) => setExistingInvoices(extractInvoices(res)))
                .catch((err) => showError(`Fehler beim Laden der ${documentLabelPlural}`, err));
        }

        await loadInvoiceMonthIndex({ force: true, suppressAuthError: options.suppressAuthError });

        const current = currentMonthRef.current;
        const currentKey = buildMonthKey(String(current.year), String(current.month));
        const expandedKeys = Object.entries(expandedInvoiceGroups?.months || {})
            .filter(([, isExpanded]) => isExpanded)
            .map(([key]) => key);
        const monthsToLoad = new Set([
            currentKey,
            ...Array.from(loadedInvoiceMonths),
            ...expandedKeys,
        ]);

        const tasks = Array.from(monthsToLoad)
            .map((key) => {
                const [year, monthKey] = key.split("-");
                if (!Number.isFinite(Number(year)) || !Number.isFinite(Number(monthKey))) return null;
                return loadInvoicesForMonthKey(year, monthKey, {
                    force: true,
                    suppressAuthError: options.suppressAuthError,
                });
            })
            .filter(Boolean);

        await Promise.all(tasks);
    };

    const schedulePostSendRefresh = (options = {}) => {
        if (postSendRefreshTimerRef.current) {
            clearTimeout(postSendRefreshTimerRef.current);
        }
        postSendRefreshTimerRef.current = setTimeout(() => {
            refreshInvoices({ suppressAuthError: options.suppressAuthError })
                .catch((err) => {
                    if (isAuthError(err)) {
                        handlePostSendAuthError();
                        return;
                    }
                    showWarning(`${documentLabel} wurde erfolgreich gespeichert, aber die Ansicht konnte nicht aktualisiert werden.`);
                });
            postSendRefreshTimerRef.current = null;
        }, 2000);
    };

    /* Ausgewählten Kundenobjekt aktualisieren */
    useEffect(() => {
        if (selectedCustomerId) {
            const customer = customers.find((c) => c.id === selectedCustomerId);
            setSelectedCustomer(customer || null);
        } else {
            setSelectedCustomer(null);
        }
    }, [selectedCustomerId, customers]);

    const customerEmail = useMemo(
        () => (selectedCustomer?.email ? selectedCustomer.email.trim() : ""),
        [selectedCustomer]
    );
    const canSendInvoiceEmail = !isOffer && Boolean(customerEmail);

    useEffect(() => {
        if (!canSendInvoiceEmail && sendInvoiceEmailEnabled) {
            setSendInvoiceEmailEnabled(false);
        }
    }, [canSendInvoiceEmail, sendInvoiceEmailEnabled]);

    const sortedCustomers = useMemo(() => {
        const list = Array.isArray(customers) ? [...customers] : [];
        const normalize = (value) => String(value || "").trim();
        const lastName = (customer) => normalize(customer?.lastname);
        const firstName = (customer) => normalize(customer?.name);
        const companyName = (customer) => normalize(customer?.companyName);
        const compareValue = (a, b) =>
            a.localeCompare(b, "de", { sensitivity: "base" });
        return list.sort((a, b) => {
            const lastA = lastName(a);
            const lastB = lastName(b);
            const hasLastA = Boolean(lastA);
            const hasLastB = Boolean(lastB);

            if (hasLastA && !hasLastB) return -1;
            if (!hasLastA && hasLastB) return 1;

            if (hasLastA && hasLastB) {
                const lastCompare = compareValue(lastA, lastB);
                if (lastCompare !== 0) return lastCompare;
                const firstCompare = compareValue(firstName(a), firstName(b));
                if (firstCompare !== 0) return firstCompare;
            } else {
                const companyCompare = compareValue(companyName(a), companyName(b));
                if (companyCompare !== 0) return companyCompare;
                const nameCompare = compareValue(firstName(a), firstName(b));
                if (nameCompare !== 0) return nameCompare;
            }

            return compareValue(normalize(a?.id), normalize(b?.id));
        });
    }, [customers]);

    const customerOptions = useMemo(
        () => buildCustomerOptions(sortedCustomers),
        [sortedCustomers]
    );

    const customersLoading = loading && customers.length === 0;

    const selectedCustomerOption = useMemo(
        () => resolveCustomerOption(customerOptions, selectedCustomerId, ""),
        [customerOptions, selectedCustomerId]
    );

    const sortedArticles = useMemo(() => {
        const list = Array.isArray(articles) ? [...articles] : [];
        const normalize = (value) => String(value || "").trim();
        const keyForArticle = (article) => {
            const name = normalize(article?.name);
            if (name) return name;
            return normalize(article?.articleNumber);
        };
        return list.sort((a, b) => {
            const keyA = keyForArticle(a);
            const keyB = keyForArticle(b);
            if (!keyA && keyB) return 1;
            if (keyA && !keyB) return -1;
            const baseCompare = keyA.localeCompare(keyB, "de", { sensitivity: "base" });
            if (baseCompare !== 0) return baseCompare;
            return normalize(a?.id).localeCompare(normalize(b?.id), "de", { sensitivity: "base" });
        });
    }, [articles]);

    const sortedShippingCosts = useMemo(() => {
        const list = Array.isArray(shippingCosts)
            ? shippingCosts.filter((cost) => cost?.active !== false)
            : [];
        const normalize = (value) => String(value || "").trim();
        return list.sort((a, b) => {
            const keyA = normalize(a?.name);
            const keyB = normalize(b?.name);
            if (!keyA && keyB) return 1;
            if (keyA && !keyB) return -1;
            const baseCompare = keyA.localeCompare(keyB, "de", { sensitivity: "base" });
            if (baseCompare !== 0) return baseCompare;
            return normalize(a?.id).localeCompare(normalize(b?.id), "de", { sensitivity: "base" });
        });
    }, [shippingCosts]);

    const hasShippingCost = invoiceArticles.some((item) => item?.shippingCostId);

    const availableWorkOrders = useMemo(() => {
        if (!selectedCustomerId) return workOrders;
        return workOrders.filter((order) => order.customerId === selectedCustomerId);
    }, [workOrders, selectedCustomerId]);
    const workOrderOptionsForInvoice = useMemo(() => {
        if (!workOrderInvoice) return [];
        const customerId = workOrderInvoice?.customer?.id || workOrderInvoice?.customerId || "";
        if (!customerId) return workOrders;
        return workOrders.filter((order) => order.customerId === customerId);
    }, [workOrders, workOrderInvoice]);

    const selectedWorkOrder = useMemo(
        () => workOrders.find((order) => order.id === selectedWorkOrderId) || null,
        [workOrders, selectedWorkOrderId]
    );

    useEffect(() => {
        if (!selectedWorkOrderId) return;
        const isValid = availableWorkOrders.some((order) => order.id === selectedWorkOrderId);
        if (!isValid) {
            setSelectedWorkOrderId("");
        }
    }, [availableWorkOrders, selectedWorkOrderId]);

    /* Zahlungsziel automatisch nach Rechnungsdatum und gewählter Frist */
    useEffect(() => {
        if (!invoiceDate || paymentTermDays === null) return; // paymentTermDays === null means manual selection
        const d = new Date(invoiceDate);
        d.setDate(d.getDate() + paymentTermDays);
        setDueDate(d.toISOString().slice(0, 10));
    }, [invoiceDate, paymentTermDays]);

    /* Hilfen */
    const showError = (msg, err) => {
        logError(msg, err, { component: "InvoiceManagement" });
        setSnack({ open: true, msg, sev: "error" });
    };
    const showWarning = (msg) => {
        logInfo(msg, { component: "InvoiceManagement", severity: "warning" });
        setSnack({ open: true, msg, sev: "warning" });
    };
    const showInfo = (msg) => {
        logInfo(msg, { component: "InvoiceManagement" });
        setSnack({ open: true, msg, sev: "success" });
    };

    const isAuthError = (err) => {
        const status = err?.response?.status;
        return status === 401 || status === 403;
    };

    const handlePostSendAuthError = () => {
        showWarning("Versandauftrag erfolgreich erzeugt. Die Sitzung ist abgelaufen oder muss erneuert werden. Bitte erneut anmelden.");
    };

    useEffect(() => {
        if (!invoicePrefill || invoicePrefillRef.current) return;
        if (loading) return;

        const customerId = invoicePrefill?.customerId || "";
        if (!customerId) {
            logError("Auftrag konnte nicht übernommen werden: Kunde fehlt.", null, {
                component: "InvoiceManagement",
            });
            setSnack({
                open: true,
                msg: "Auftrag konnte nicht übernommen werden: Kunde fehlt.",
                sev: "error",
            });
            invoicePrefillRef.current = true;
            return;
        }

        const items = Array.isArray(invoicePrefill.items) ? invoicePrefill.items : [];
        const resolvedItems = [];
        let skipped = 0;
        let missingArticles = 0;

        items.forEach((item) => {
            const amount = toNumberSafe(item?.amount);
            if (!item?.articleId || amount <= 0) {
                skipped += 1;
                return;
            }
            const discountValue = toNumberSafe(item?.articleDiscount);
            const articleDiscount = discountValue > 0 ? discountValue : 0;
            const articleDiscountReason = item?.articleDiscountReason || "";
            const article = articles.find((entry) => entry.id === item.articleId);
            if (!article) {
                missingArticles += 1;
                resolvedItems.push({
                    id: item.articleId,
                    name: item.articleName || "Unbekannter Artikel",
                    price: 0,
                    taxRate: 0,
                    amount,
                    articleDiscount,
                    articleDiscountReason,
                    workOrderTimeEntryId: item.workOrderTimeEntryId || null,
                    workOrderMaterialEntryId: item.workOrderMaterialEntryId || null,
                });
                return;
            }
            resolvedItems.push({
                ...article,
                amount,
                articleDiscount,
                articleDiscountReason,
                workOrderTimeEntryId: item.workOrderTimeEntryId || null,
                workOrderMaterialEntryId: item.workOrderMaterialEntryId || null,
            });
        });

        setSelectedCustomerId(customerId);
        const matchedOrder = invoicePrefill.orderId
            ? workOrders.find((order) => order.id === invoicePrefill.orderId)
            : null;
        setSelectedWorkOrderId(matchedOrder ? matchedOrder.id : "");

        setInvoiceArticles(normalizeInvoiceArticles(resolvedItems));
        setInvoiceType(isOffer ? "OFFER" : "STANDARD");
        setParentInvoiceId("");
        setOfferParentId(invoicePrefill?.offerId || "");
        setPartPercent("");
        setBaseAmountOverride("");
        setParentInvoice(null);
        if (invoicePrefill?.totalDiscount && invoicePrefill.totalDiscount.type) {
            setTotalDiscountType(invoicePrefill.totalDiscount.type);
            setTotalDiscountValue(toNumberSafe(invoicePrefill.totalDiscount.value));
        } else {
            setTotalDiscountType("none");
            setTotalDiscountValue(0);
        }
        setSelectedArticleId("");
        setSelectedShippingCostId("");
        setSelectedAmount(1);
        setArticleDiscount(false);
        setArticleDiscountPercent(0);
        setArticleDiscountReason("");
        setOpenDialog(true);

        if (resolvedItems.length === 0) {
            setSnack({
                open: true,
                msg: "Keine abrechenbaren Positionen gefunden.",
                sev: "warning",
            });
        } else if (missingArticles > 0 || skipped > 0) {
            setSnack({
                open: true,
                msg: "Einige Positionen konnten nicht uebernommen werden.",
                sev: "warning",
            });
        }

        invoicePrefillRef.current = true;
    }, [invoicePrefill, loading, articles, workOrders, isOffer]);

    useEffect(() => {
        if (typeof window === "undefined") return;
        try {
            sessionStorage.setItem(invoiceGroupStateKey, JSON.stringify(expandedInvoiceGroups));
        } catch {
            // Session storage is optional; ignore write failures.
        }
    }, [expandedInvoiceGroups]);

    const resolvePaymentCurrency = (invoice) => {
        const candidate = invoice?.currency || profil?.baseCurrency || "EUR";
        if (typeof candidate === "string" && candidate.trim()) {
            return candidate.trim().toUpperCase();
        }
        return "EUR";
    };
    const resolveInvoiceCustomer = (invoice) => {
        if (!invoice) return null;
        return invoice.customer || customers.find((customer) => customer.id === invoice.customerId) || null;
    };
    const resolveInvoiceCustomerName = (invoice) => {
        const customer = resolveInvoiceCustomer(invoice);
        const name = [customer?.name, customer?.lastname].filter(Boolean).join(" ");
        return [customer?.companyName, name]
            .filter((value) => value && String(value).trim())
            .map((value) => String(value).trim())
            .find(Boolean) || "";
    };

    const handleConvertOfferToInvoice = (offer) => {
        if (!offer) return;
        const offerHasInvoice =
            Array.isArray(offer.children) &&
            offer.children.some((child) => child?.invoiceType !== "CANCELLATION");
        if (offerHasInvoice) {
            showError("Angebot wurde bereits abgerechnet.");
            return;
        }
        const customerId = offer.customerId || offer.customer?.id || "";
        if (!customerId) {
            showError("Angebot kann nicht uebernommen werden: Kunde fehlt.");
            return;
        }
        const items = Array.isArray(offer.articles) ? offer.articles : [];
        const invoiceItems = items
            .map((item) => ({
                articleId: item.articleId,
                amount: item.amount,
                articleName: item.description,
                articleDiscount: item.articleDiscount,
                articleDiscountReason: item.articleDiscountReason,
                workOrderTimeEntryId: item.workOrderTimeEntryId || null,
                workOrderMaterialEntryId: item.workOrderMaterialEntryId || null,
            }))
            .filter((item) => item.articleId && toNumberSafe(item.amount) > 0);

        if (invoiceItems.length === 0) {
            showError("Angebot kann nicht uebernommen werden: Keine Positionen gefunden.");
            return;
        }

        const totalDiscount = offer.totalDiscountType
            ? { type: offer.totalDiscountType, value: offer.totalDiscountValue }
            : null;

        navigate("/invoices", {
            state: {
                invoicePrefill: {
                    offerId: offer.id,
                    customerId,
                    items: invoiceItems,
                    totalDiscount,
                },
            },
        });
    };

    // eslint-disable-next-line no-unused-vars
    const handleNextStep = () => {
        const nextErrors = {};
        if (activeStep === 0 && !isOffer) {
            const requiresParent = invoiceType !== "STANDARD";
            if (requiresParent && !parentInvoiceId) {
                nextErrors.parentInvoiceId = "Bitte eine Bezugsrechnung auswählen.";
                setFieldErrors((prev) => ({ ...prev, ...nextErrors }));
                showError("Bitte wähle eine Bezugsrechnung für diese Teil-/Schlussrechnung.");
                return;
            }
        }
        if (activeStep === 1) {
            if (!selectedCustomerId) {
                nextErrors.customerId = "Bitte Kunden auswaehlen.";
                setFieldErrors((prev) => ({ ...prev, ...nextErrors }));
                showError("Bitte Kunden auswaehlen.");
                return;
            }
            if (invoiceArticles.length === 0) {
                nextErrors.articles = "Bitte mindestens eine Position hinzufügen.";
                setFieldErrors((prev) => ({ ...prev, ...nextErrors }));
                showError("Bitte mindestens eine Position hinzufügen.");
                return;
            }
        }
        setActiveStep((prev) => Math.min(prev + 1, wizardSteps.length - 1));
    };

    // eslint-disable-next-line no-unused-vars
    const handlePrevStep = () => {
        setActiveStep((prev) => Math.max(prev - 1, 0));
    };

    const hasAnyInvoices = isOffer
        ? existingInvoices.length > 0
        : invoiceMonthIndexLoaded
            ? invoiceMonthIndex.length > 0
            : existingInvoices.length > 0;

    const hasInvoices = hasAnyInvoices;
    const cancelledInvoiceIds = useMemo(() => {
        const ids = new Set();
        const stack = Array.isArray(existingInvoices) ? [...existingInvoices] : [];
        while (stack.length > 0) {
            const invoice = stack.pop();
            if (!invoice) continue;
            if (invoice.invoiceType === "CANCELLATION") {
                if (invoice.id) ids.add(invoice.id);
                if (invoice.parentInvoiceId) ids.add(invoice.parentInvoiceId);
            }
            if (Array.isArray(invoice.children) && invoice.children.length > 0) {
                stack.push(...invoice.children);
            }
        }
        return ids;
    }, [existingInvoices]);
    const parentInvoiceOptions = useMemo(
        () => existingInvoices.filter((inv) => inv?.id && !cancelledInvoiceIds.has(inv.id)),
        [existingInvoices, cancelledInvoiceIds]
    );

    const filteredInvoices = useMemo(() => {
        if (isOffer) return existingInvoices;
        if (payLinkFilter === "ALL") return existingInvoices;
        return existingInvoices.filter((inv) => {
            const status = inv?.paymentLinkStatus;
            if (!status) return false;
            if (payLinkFilter === "SENT") {
                return status === "SENT" || status === "CREATED";
            }
            if (payLinkFilter === "PAID") {
                return status === "PAID";
            }
            return true;
        });
    }, [existingInvoices, payLinkFilter, isOffer]);

    const availableInvoiceMonths = useMemo(() => {
        if (isOffer || !invoiceMonthIndexLoaded) return [];
        const months = Array.isArray(invoiceMonthIndex) ? [...invoiceMonthIndex] : [];
        const current = currentMonthRef.current;
        const hasCurrent = months.some(
            (entry) => entry.year === current.year && entry.month === current.month
        );
        if (!hasCurrent) {
            months.push({ year: current.year, month: current.month });
        }
        return months;
    }, [invoiceMonthIndex, invoiceMonthIndexLoaded, isOffer]);



    const isInvoiceLoading = loading || (!isOffer && loadingInvoiceMonths.size > 0);

    const toggleYearExpanded = (year) => {
        setExpandedInvoiceGroups((prev) => {
            const years = { ...(prev?.years || {}) };
            years[year] = !years[year];
            return { years, months: prev?.months || {} };
        });
    };

    const toggleMonthExpanded = (year, monthKey) => {
        const key = `${year}-${monthKey}`;
        const isExpanded = !!expandedInvoiceGroups?.months?.[key];
        setExpandedInvoiceGroups((prev) => {
            const months = { ...(prev?.months || {}) };
            months[key] = !months[key];
            return { years: prev?.years || {}, months };
        });
        if (!isExpanded) {
            loadInvoicesForMonthKey(year, monthKey);
        }
    };

    /* Gruppieren der vorhandenen Rechnungen nach Jahr & Monat */
    const groupedInvoices = useMemo(() => {
        const groups = {};
        if (!isOffer && availableInvoiceMonths.length > 0) {
            availableInvoiceMonths.forEach(({ year, month }) => {
                const yearKey = String(year);
                const monthKey = String(month);
                if (!groups[yearKey]) groups[yearKey] = {};
                if (!groups[yearKey][monthKey]) groups[yearKey][monthKey] = [];
            });
        }
        filteredInvoices.forEach((inv) => {
            const date = inv?.invoiceDate ? new Date(inv.invoiceDate) : null;
            const isValidDate = date && !Number.isNaN(date.getTime());
            const yearKey = isValidDate ? String(date.getFullYear()) : "Unbekannt";
            const monthKey = isValidDate ? String(date.getMonth()) : "Unbekannt";
            if (!groups[yearKey]) groups[yearKey] = {};
            if (!groups[yearKey][monthKey]) groups[yearKey][monthKey] = [];
            groups[yearKey][monthKey].push(inv);
        });
        return groups;
    }, [filteredInvoices, availableInvoiceMonths, isOffer]);
    const sortedYears = useMemo(() => {
        return Object.keys(groupedInvoices).sort((a, b) => {
            if (a === "Unbekannt") return 1;
            if (b === "Unbekannt") return -1;
            return Number(b) - Number(a);
        });
    }, [groupedInvoices]);

    const showEmptyState = !isInvoiceLoading && (
        (isOffer && sortedYears.length === 0) ||
        (!isOffer && invoiceMonthIndexLoaded && invoiceMonthIndex.length === 0 && filteredInvoices.length === 0)
    );

    const invoiceTree = useMemo(() => {
        const invoices = (existingInvoices || []).filter(
            (inv) => inv?.invoiceType !== "CANCELLATION"
        );
        const byId = new Map();
        invoices.forEach((inv) => {
            if (inv?.id) byId.set(inv.id, inv);
        });
        const offerIds = new Set();
        invoices.forEach((inv) => {
            if (inv?.invoiceType === "OFFER" && inv?.id) offerIds.add(inv.id);
        });
        const childMap = new Map();
        invoices.forEach((inv) => {
            if (inv?.parentInvoiceId && byId.has(inv.parentInvoiceId)) {
                const list = childMap.get(inv.parentInvoiceId) || [];
                list.push(inv.id);
                childMap.set(inv.parentInvoiceId, list);
            }
        });
        const excludeIds = new Set();
        const stack = [];
        const pushExclude = (id) => {
            if (!id || excludeIds.has(id)) return;
            excludeIds.add(id);
            stack.push(id);
        };
        invoices.forEach((inv) => {
            const parentIsOffer =
                inv?.parentInvoiceType === "OFFER" ||
                (inv?.parentInvoiceId && offerIds.has(inv.parentInvoiceId));
            if ((inv?.invoiceType === "OFFER" || parentIsOffer) && inv?.id) {
                pushExclude(inv.id);
            }
        });
        while (stack.length) {
            const id = stack.pop();
            const entry = byId.get(id);
            const parentId = entry?.parentInvoiceId;
            if (parentId && byId.has(parentId)) pushExclude(parentId);
            const children = childMap.get(id) || [];
            children.forEach((childId) => pushExclude(childId));
        }
        const candidates = invoices.filter((inv) => inv?.id && !excludeIds.has(inv.id));
        const parentIds = new Set();
        candidates.forEach((inv) => {
            if (inv?.parentInvoiceId) parentIds.add(inv.parentInvoiceId);
        });
        const projectInvoices = candidates.filter(
            (inv) => inv?.parentInvoiceId || parentIds.has(inv?.id)
        );
        const nodeMap = new Map();
        projectInvoices.forEach((inv) => {
            nodeMap.set(inv.id, { ...inv, children: [] });
        });
        const sortNodes = (nodes) =>
            nodes.sort((a, b) => {
                const dateA = a.invoiceDate ? new Date(a.invoiceDate).getTime() : 0;
                const dateB = b.invoiceDate ? new Date(b.invoiceDate).getTime() : 0;
                return dateA - dateB;
            });
        nodeMap.forEach((node) => {
            if (node.parentInvoiceId && nodeMap.has(node.parentInvoiceId)) {
                nodeMap.get(node.parentInvoiceId).children.push(node);
            }
        });
        nodeMap.forEach((node) => {
            node.children = sortNodes(node.children);
        });
        const roots = [];
        nodeMap.forEach((node) => {
            if (!node.parentInvoiceId || !nodeMap.has(node.parentInvoiceId)) {
                roots.push(node);
            }
        });
        return sortNodes(roots);
    }, [existingInvoices]);

    /* Artikel hinzufügen */
    const handleAddArticle = () => {
        let enriched;
        const parsedAmount = parsePositiveDecimalInput(selectedAmount);
        if (!parsedAmount.value || parsedAmount.error) {
            showError("Bitte eine gueltige Menge eingeben (z.B. 1,5).");
            return;
        }
        const amount = parsedAmount.value;
        if (isFreeText) {
            if (!freeTextName || freeTextPrice === "") return;
            enriched = {
                id: null,
                isFreeText: true,
                name: freeTextName,
                price: parseFloat(String(freeTextPrice).replace(",", ".")) || 0,
                taxRate: parseFloat(String(freeTextTaxRate).replace(",", ".")) || 0,
                unit: freeTextUnit,
                amount: amount,
                articleDiscount: useArticleDiscount ? (articleDiscountPercent || 0) : 0,
                articleDiscountReason: useArticleDiscount ? (articleDiscountReason || "") : "",
            };
        } else {
            const selected = articles.find((a) => a.id === selectedArticleId);
            if (!selected) return;

            enriched = {
                ...selected,
                amount: amount,
                articleDiscount: useArticleDiscount ? (articleDiscountPercent || 0) : 0,
                articleDiscountReason: useArticleDiscount ? (articleDiscountReason || "") : "",
            };
        }

        setInvoiceArticles((prev) => normalizeInvoiceArticles([...prev, enriched]));
        clearFieldError("articles");

        // reset
        setSelectedArticleId("");
        setFreeTextName("");
        setFreeTextPrice("");
        setFreeTextTaxRate(19);
        setFreeTextUnit("Stk");
        setSelectedAmount("1");
        setArticleDiscount(false);
        setArticleDiscountPercent(0);
        setArticleDiscountReason("");
    };

    /* Versandkosten verwalten */
    const applyShippingCost = (shippingCostId) => {
        const selected = shippingCosts.find((cost) => cost.id === shippingCostId);
        if (!selected) return false;
        const enriched = buildShippingCostArticle(selected);
        setInvoiceArticles((prev) => normalizeInvoiceArticles([...prev, enriched]));
        clearFieldError("articles");
        return true;
    };

    const handleToggleShippingCost = (checked) => {
        if (!checked) {
            setInvoiceArticles((prev) => prev.filter((item) => !item?.shippingCostId));
            return;
        }
        const fallbackId = selectedShippingCostId || sortedShippingCosts[0]?.id || "";
        if (!fallbackId) {
            showError("Keine Versandkosten vorhanden.");
            return;
        }
        if (!selectedShippingCostId) {
            setSelectedShippingCostId(fallbackId);
        }
        if (!applyShippingCost(fallbackId)) {
            showError("Versandkosten nicht gefunden.");
        }
    };

    const handleShippingCostSelectionChange = (value) => {
        setSelectedShippingCostId(value);
        if (!hasShippingCost) return;
        if (!value) {
            setInvoiceArticles((prev) => prev.filter((item) => !item?.shippingCostId));
            return;
        }
        if (!applyShippingCost(value)) {
            showError("Versandkosten nicht gefunden.");
        }
    };

    /* Rechnungsnummer */
    const generateInvoiceNumber = (options = {}) => {
        const y = new Date(invoiceDate).getFullYear();
        getInvoiceNextNumber(y, isOffer ? "OFFER" : undefined, {
            suppressAuthError: options.suppressAuthError,
        })
            .then((res) => setInvoiceNextNumber(res.data))
            .catch((err) => {
                if (options.suppressAuthError && isAuthError(err)) {
                    handlePostSendAuthError();
                    return;
                }
                showError(nextNumberErrorMessage, err);
            });
    };

    /* Rechnungsvorschau */
    const handlePreviewInvoice = async () => {
        const normalizedArticles = normalizeInvoiceArticles(invoiceArticles);
        const requiresParent = !isOffer && invoiceType !== "STANDARD";
        const partPercentValue = parseNumberInput(partPercent);
        const nextErrors = {};
        if (!selectedCustomerId) {
            nextErrors.customerId = "Bitte Kunden auswaehlen.";
        }
        if (!invoiceDate) {
            nextErrors.invoiceDate = `Bitte ${documentDateLabel} auswählen.`;
        }
        if (!dueDate) {
            nextErrors.dueDate = `Bitte ${documentDueLabel} auswählen.`;
        }
        if (requiresParent && !parentInvoiceId) {
            nextErrors.parentInvoiceId = "Bitte eine Bezugsrechnung auswählen.";
        }
        if (normalizedArticles.length === 0) {
            nextErrors.articles = "Bitte mindestens eine Position hinzufügen.";
        }
        if ((invoiceType === "DOWN_PAYMENT" || invoiceType === "PARTIAL") && (!partPercentValue || partPercentValue <= 0)) {
            nextErrors.partPercent = "Bitte einen Abrechnungsanteil größer 0 eingeben.";
        }
        if (Object.keys(nextErrors).length > 0) {
            setFieldErrors(nextErrors);
            showError("Bitte alle erforderlichen Felder ausfüllen, um eine Vorschau zu generieren.");
            return;
        }
        setFieldErrors({});
        setArticleErrors({});

        const baseOverrideNumber = parseNumberInput(baseAmountOverride);
        const parentBase = parentInvoice ? parentInvoice.baseAmount ?? parentInvoice.totalGross ?? null : null;
        const treatAsStandard = isOffer || invoiceType === "STANDARD";
        const baseAmount = treatAsStandard
            ? baseOverrideNumber ?? totals.totalGross
            : baseOverrideNumber ?? parentBase;
        const normalizedFreeText = invoiceFreeText.trim();

        const resolvedCurrency = (() => {
            const candidate = (profil?.baseCurrency || "EUR").trim();
            return candidate ? candidate.toUpperCase() : "EUR";
        })();

        const resolvedInvoiceType = isOffer ? "OFFER" : invoiceType;
        const resolvedParentInvoiceId = !isOffer && invoiceType !== "STANDARD"
            ? parentInvoiceId || null
            : offerParentId || null;

        const invoicePayload = {
            customerId: selectedCustomerId,
            invoiceDate,
            dueDate,
            performanceDate,
            performanceLocation:
                selectedLocation && typeof selectedLocation === "object"
                    ? {
                        street: selectedLocation.street,
                        zip: selectedLocation.zip,
                        city: selectedLocation.city,
                    }
                    : null,
            articles: normalizedArticles.map((a) => ({
                articleId: (a.shippingCostId || a.isFreeText) ? null : a.id,
                shippingCostId: a.shippingCostId || null,
                amount: a.amount,
                articleDiscount: a.articleDiscount || 0,
                articleDiscountReason: a.articleDiscountReason || "",
                workOrderTimeEntryId: a.workOrderTimeEntryId,
                workOrderMaterialEntryId: a.workOrderMaterialEntryId,
                description: a.isFreeText ? a.name : null,
                unitPrice: a.isFreeText ? a.price : null,
                taxRate: a.isFreeText ? a.taxRate : null,
                unit: a.isFreeText ? a.unit : null,
            })),
            totalDiscount: {
                type: totalDiscountType,
                value: totalDiscountValue,
            },
            freeText: normalizedFreeText || null,
            invoiceType: resolvedInvoiceType,
            parentInvoiceId: resolvedParentInvoiceId,
            partPercent: partPercentValue,
            baseAmount: baseAmount != null ? Number(baseAmount) : null,
            workOrderId: selectedWorkOrderId || null,
            currency: resolvedCurrency,
            isReverseCharge,
        };

        try {
            const res = await previewInvoicePdf(invoicePayload);
            const blob = res?.data;
            if (!(blob instanceof Blob)) {
                throw new Error("Kein PDF erhalten");
            }
            const url = window.URL.createObjectURL(blob);
            window.open(url, "_blank", "noopener");
            setTimeout(() => window.URL.revokeObjectURL(url), 30_000);
            showInfo("PDF-Vorschau geoeffnet.");
        } catch (err) {
            showError("Fehler beim Erstellen der PDF-Vorschau.", err);
        }
    };

    /* Rechnung erzeugen */
    const handleCreateInvoice = async () => {
        const normalizedArticles = normalizeInvoiceArticles(invoiceArticles);
        const requiresParent = !isOffer && invoiceType !== "STANDARD";
        const partPercentValue = parseNumberInput(partPercent);
        const nextErrors = {};
        if (!selectedCustomerId) {
            nextErrors.customerId = "Bitte Kunden auswaehlen.";
        }
        if (!invoiceDate) {
            nextErrors.invoiceDate = `Bitte ${documentDateLabel} auswählen.`;
        }
        if (!dueDate) {
            nextErrors.dueDate = `Bitte ${documentDueLabel} auswählen.`;
        }
        if (requiresParent && !parentInvoiceId) {
            nextErrors.parentInvoiceId = "Bitte eine Bezugsrechnung auswählen.";
        }
        if (normalizedArticles.length === 0) {
            nextErrors.articles = "Bitte mindestens eine Position hinzufügen.";
        }
        if ((invoiceType === "DOWN_PAYMENT" || invoiceType === "PARTIAL") && (!partPercentValue || partPercentValue <= 0)) {
            nextErrors.partPercent = "Bitte einen Abrechnungsanteil größer 0 eingeben.";
        }
        if (Object.keys(nextErrors).length > 0) {
            setFieldErrors(nextErrors);
            return;
        }
        setFieldErrors({});
        setArticleErrors({});

        const baseOverrideNumber = parseNumberInput(baseAmountOverride);
        const parentBase = parentInvoice ? parentInvoice.baseAmount ?? parentInvoice.totalGross ?? null : null;
        const treatAsStandard = isOffer || invoiceType === "STANDARD";
        const baseAmount = treatAsStandard
            ? baseOverrideNumber ?? totals.totalGross
            : baseOverrideNumber ?? parentBase;
        const normalizedFreeText = invoiceFreeText.trim();

        const resolvedCurrency = (() => {
            const candidate = (profil?.baseCurrency || "EUR").trim();
            return candidate ? candidate.toUpperCase() : "EUR";
        })();

        const resolvedInvoiceType = isOffer ? "OFFER" : invoiceType;
        const resolvedParentInvoiceId = !isOffer && invoiceType !== "STANDARD"
            ? parentInvoiceId || null
            : offerParentId || null;
            const invoicePayload = {
                customerId: selectedCustomerId,
                invoiceDate,
                dueDate,
                performanceDate,
            performanceLocation:
                selectedLocation && typeof selectedLocation === "object"
                    ? {
                        street: selectedLocation.street,
                        zip: selectedLocation.zip,
                        city: selectedLocation.city,
                    }
                    : null,
            articles: normalizedArticles.map((a) => ({
                articleId: (a.shippingCostId || a.isFreeText) ? null : a.id,
                shippingCostId: a.shippingCostId || null,
                amount: a.amount,
                articleDiscount: a.articleDiscount || 0,
                articleDiscountReason: a.articleDiscountReason || "",
                workOrderTimeEntryId: a.workOrderTimeEntryId,
                workOrderMaterialEntryId: a.workOrderMaterialEntryId,
                description: a.isFreeText ? a.name : null,
                unitPrice: a.isFreeText ? a.price : null,
                taxRate: a.isFreeText ? a.taxRate : null,
                unit: a.isFreeText ? a.unit : null,
            })),
            totalDiscount: {
                type: totalDiscountType,
                value: totalDiscountValue,
            },
            freeText: normalizedFreeText || null,
            invoiceType: resolvedInvoiceType,
            parentInvoiceId: resolvedParentInvoiceId,
            partPercent: partPercentValue,
            baseAmount: baseAmount != null ? Number(baseAmount) : null,
            workOrderId: selectedWorkOrderId || null,
              currency: resolvedCurrency,
              createXRechnung: !isOffer && createXRechnung,
            isPaid,
            isReverseCharge,
            };
            const shouldSendEmail = sendInvoiceEmailEnabled && canSendInvoiceEmail;
            try {
                const res = await createInvoice(invoicePayload);
                const createdInvoice = res?.data;
                showInfo(`${documentLabel} erfolgreich erstellt`);
                if (shouldSendEmail && createdInvoice?.id) {
                    try {
                        if (isKeycloakMode()) {
                            await ensureFreshToken(60);
                        }
                        await sendInvoiceEmail(createdInvoice.id, { suppressAuthError: true });
                        suppressAuthEscalationFor(
                            POST_SEND_AUTH_ESCALATION_SUPPRESSION_MS,
                            'invoice-email-success'
                        );
                        showInfo("E-Mail Versand angestoßen");
                    } catch (err) {
                        if (isAuthError(err)) {
                            handlePostSendAuthError();
                        } else {
                            showError("E-Mail Versand fehlgeschlagen", err);
                        }
                    }
                }
                handleCloseCreateDialog();
                try {
                    await refreshInvoices({ suppressAuthError: shouldSendEmail });
                } catch (err) {
                    if (shouldSendEmail && isAuthError(err)) {
                        handlePostSendAuthError();
                        return;
                    } else {
                        showWarning(`${documentLabel} wurde erfolgreich erstellt, aber die Liste konnte nicht aktualisiert werden.`);
                    }
                }
                if (shouldSendEmail) {
                    schedulePostSendRefresh({ suppressAuthError: true });
                }
                generateInvoiceNumber({ suppressAuthError: shouldSendEmail });
            } catch (err) {
            const backendError = err?.response?.data;
            const details = Array.isArray(backendError?.details) ? backendError.details : null;
            if (details && details.length > 0) {
                const nextFieldErrors = {};
                const nextArticleErrors = {};
                details.forEach((detail) => {
                    const rawField = detail?.field;
                    const message = detail?.message;
                    if (!rawField || !message) return;
                    const articleMatch = /^articles\\[(\\d+)\\]\\.(.+)$/.exec(rawField);
                    if (articleMatch) {
                        const index = Number(articleMatch[1]);
                        const field = articleMatch[2];
                        nextArticleErrors[index] = {
                            ...(nextArticleErrors[index] || {}),
                            [field]: message,
                        };
                        return;
                    }
                    if (rawField === "performanceLocation" || rawField.startsWith("performanceLocation.")) {
                        nextFieldErrors.performanceLocation = message;
                        return;
                    }
                    if (rawField === "totalDiscount.value" || rawField === "totalDiscountValue") {
                        nextFieldErrors.totalDiscountValue = message;
                        return;
                    }
                    if (rawField === "totalDiscount.type" || rawField === "totalDiscountType") {
                        nextFieldErrors.totalDiscountType = message;
                        return;
                    }
                    if (rawField === "baseAmount") {
                        nextFieldErrors.baseAmount = message;
                        return;
                    }
                    nextFieldErrors[rawField] = message;
                });
                setFieldErrors(nextFieldErrors);
                setArticleErrors(nextArticleErrors);
            }
            showError(`Fehler beim Erstellen der ${documentLabel}`, err);
        }
    };

    useEffect(() => {
        return () => {
            if (postSendRefreshTimerRef.current) {
                clearTimeout(postSendRefreshTimerRef.current);
            }
        };
    }, []);

    const handleCloseCreateDialog = () => {
        resetForm();
        setOpenDialog(false);
    };

    const resetForm = () => {
        setSelectedCustomerId("");
        setSelectedWorkOrderId("");
        setSelectedArticleId("");
        setSelectedShippingCostId("");
        setIsFreeText(false);
        setFreeTextName("");
        setFreeTextPrice("");
        setFreeTextTaxRate(19);
        setFreeTextUnit("Stk");
        setInvoiceArticles([]);
        setSelectedCustomer(null);
        setSelectedLocation("");
        setTotalDiscountType("none");
        setTotalDiscountValue(0);
        setInvoiceType("STANDARD");
        setParentInvoiceId("");
        setOfferParentId("");
        setPartPercent("");
        setBaseAmountOverride("");
        setInvoiceFreeText("");
        setParentInvoice(null);
        setCreateXRechnung(!isOffer);
        setSendInvoiceEmailEnabled(false);
        setIsPaid(false);
        setActiveStep(0);
        setFieldErrors({});
        setArticleErrors({});
        setPaymentTermDays(7);
        setIsReverseCharge(false);
    };

    const clearFieldError = (field) => {
        setFieldErrors((prev) => {
            if (!prev[field]) return prev;
            const next = { ...prev };
            delete next[field];
            return next;
        });
    };

    const getArticleErrorSummary = (index) => {
        const entry = articleErrors[index];
        if (!entry) return "";
        return Object.values(entry).filter(Boolean).join(" · ");
    };

    /* Bezahlt/Ungedreht */

    const handlePaidChange = (invoice, checked) => {
        if (checked) {
            setPaymentInvoice(invoice);
            setPaymentDate(new Date().toISOString().slice(0, 10));
            setPaymentDialogOpenState(true);
        } else {
            updateInvoicePaid(invoice.id, { paid: false, paymentDate: null, version: invoice.version })
                .then(() => refreshInvoices())
                .catch((err) => showError("Fehler beim Aktualisieren der Zahlung", err));
        }
    };

    const handleSentChange = (invoice, checked) => {
        if (!invoice?.id) return;
        updateInvoiceSent(invoice.id, { sent: checked, version: invoice.version })
            .then(() => refreshInvoices())
            .catch((err) => showError("Fehler beim Aktualisieren des Versandstatus", err));
    };

    const handleConfirmPayment = () => {
        if (!paymentInvoice) return;
        const totalGross =
            paymentInvoice.totalGross !== null && paymentInvoice.totalGross !== undefined
                ? toNumberSafe(paymentInvoice.totalGross)
                : null;
        const paidSoFar = getInvoicePaidAmount(paymentInvoice);
        const paymentAmount =
            totalGross != null ? roundMoney(Math.max(paidSoFar, totalGross)) : paidSoFar || null;
        const paymentCurrency = paymentAmount != null ? resolvePaymentCurrency(paymentInvoice) : null;
        updateInvoicePaid(paymentInvoice.id, {
            paid: true,
            paymentDate,
            paymentAmount,
            paymentCurrency,
            version: paymentInvoice.version,
        })
            .then(() => refreshInvoices())
            .then(() => setSnack({ open: true, msg: "Zahlung gespeichert", sev: "success" }))
            .catch((err) => showError("Fehler beim Aktualisieren der Zahlung", err))
            .finally(() => {
                setPaymentDialogOpenState(false);
                setPaymentInvoice(null);
            });
    };

    const handleOpenPartialPayment = (invoice) => {
        if (!invoice) return;
        setPartialPaymentInvoice(invoice);
        setPartialPaymentAmount("");
        setPartialPaymentDate(new Date().toISOString().slice(0, 10));
        setPartialPaymentDialogOpen(true);
    };

    const handleConfirmPartialPayment = () => {
        if (!partialPaymentInvoice) return;
        const amount = parseNumberInput(partialPaymentAmount);
        if (amount == null || amount <= 0) {
            showError("Bitte einen Teilzahlungsbetrag eingeben.");
            return;
        }
        const paidSoFar = getInvoicePaidAmount(partialPaymentInvoice);
        const newTotal = roundMoney(paidSoFar + amount);
        const totalGross =
            partialPaymentInvoice.totalGross !== null && partialPaymentInvoice.totalGross !== undefined
                ? toNumberSafe(partialPaymentInvoice.totalGross)
                : null;
        if (totalGross != null && totalGross > 0) {
            const outstanding = roundMoney(totalGross - paidSoFar);
            if (outstanding <= 0) {
                showError("Rechnung ist bereits vollständig bezahlt.");
                return;
            }
            if (amount > outstanding + 0.01) {
                showError("Teilzahlung übersteigt den offenen Betrag.");
                return;
            }
        }
        const fullyPaid =
            totalGross != null && totalGross > 0 ? newTotal >= totalGross - 0.01 : false;
        const paymentCurrency = resolvePaymentCurrency(partialPaymentInvoice);
        updateInvoicePaid(partialPaymentInvoice.id, {
            paid: fullyPaid,
            paymentDate: partialPaymentDate,
            paymentAmount: newTotal,
            paymentCurrency,
            version: partialPaymentInvoice.version,
        })
            .then(() => refreshInvoices())
            .then(() =>
                showInfo(fullyPaid ? "Zahlung vollständig erfasst." : "Teilzahlung gespeichert.")
            )
            .catch((err) => showError("Fehler beim Speichern der Teilzahlung", err))
            .finally(() => {
                setPartialPaymentDialogOpen(false);
                setPartialPaymentInvoice(null);
                setPartialPaymentAmount("");
            });
    };

    const handleCancelPartialPayment = () => {
        setPartialPaymentDialogOpen(false);
        setPartialPaymentInvoice(null);
        setPartialPaymentAmount("");
    };

    const handleCancelPayment = () => {
        setPaymentDialogOpenState(false);
        setPaymentInvoice(null);
    };

    const handleOpenWorkOrderDialog = (invoice) => {
        if (!invoice) return;
        setWorkOrderInvoice(invoice);
        setWorkOrderSelection(invoice.workOrderId || "");
        setWorkOrderDialogOpen(true);
    };

    const handleCloseWorkOrderDialog = () => {
        setWorkOrderDialogOpen(false);
        setWorkOrderInvoice(null);
        setWorkOrderSelection("");
    };

    const handleConfirmWorkOrder = async () => {
        if (!workOrderInvoice) return;
        try {
            await updateInvoiceWorkOrder(workOrderInvoice.id, {
                version: workOrderInvoice.version ?? null,
                workOrderId: workOrderSelection || null,
            });
            showInfo("Auftrag aktualisiert");
            handleCloseWorkOrderDialog();
            await refreshInvoices();
        } catch (err) {
            showError("Fehler beim Aktualisieren des Auftrags", err);
        }
    };

    const handleOpenCancelDialog = (invoice) => {
        if (!invoice?.id) return;
        setCancelInvoiceTarget(invoice);
        setCancelDialogOpen(true);
    };

    const handleCloseCancelDialog = () => {
        if (cancelPending) return;
        setCancelDialogOpen(false);
        setCancelInvoiceTarget(null);
    };

    const handleConfirmCancelInvoice = async () => {
        if (!cancelInvoiceTarget?.id) return;
        try {
            setCancelPending(true);
            await cancelInvoice(cancelInvoiceTarget.id);
            showInfo("Storno-Rechnung erstellt");
            setCancelDialogOpen(false);
            setCancelInvoiceTarget(null);
            await refreshInvoices();
        } catch (err) {
            showError("Storno-Rechnung konnte nicht erstellt werden", err);
        } finally {
            setCancelPending(false);
        }
    };

    const handleCreatePayLink = (invoice) => {
        if (!invoice?.id) return;
        createInvoicePaymentLink(invoice.id)
            .then((res) => {
                const status = res?.data?.status || "SENT";
                const message = status === "PAID" ? "Zahlung bereits bestätigt." : "Pay-Link erstellt.";
                showInfo(message);
                refreshInvoices();
            })
            .catch((err) => showError("Pay-Link konnte nicht erstellt werden", err));
    };

    const handleCopyPayLink = async (invoice) => {
        if (!invoice?.paymentLinkUrl) {
            showError("Kein Pay-Link vorhanden.");
            return;
        }
        try {
            if (!navigator?.clipboard) {
                throw new Error("Clipboard API nicht verfuegbar");
            }
            await navigator.clipboard.writeText(invoice.paymentLinkUrl);
            showInfo("Pay-Link kopiert");
        } catch (err) {
            showError("Pay-Link konnte nicht kopiert werden", err);
        }
    };

    /* Totals */
    const totals = useMemo(() => {
        const netByRate = invoiceArticles.reduce((acc, a) => {
            const base = (a.price || 0) * (a.amount || 0);
            const net = a.articleDiscount ? base * (1 - (a.articleDiscount || 0) / 100) : base;
            const rate = Number(a.taxRate || 0);
            acc[rate] = (acc[rate] || 0) + net;
            return acc;
        }, {});

        const baseNet = Object.values(netByRate).reduce((sum, v) => sum + v, 0);

        let discountAmount = 0;
        if (totalDiscountType === "percent") {
            discountAmount = baseNet * ((totalDiscountValue || 0) / 100);
        } else if (totalDiscountType === "fixed") {
            discountAmount = totalDiscountValue || 0;
        }

        const totalNet = baseNet - discountAmount;

        const netByRateAfterDiscount = {};
        Object.entries(netByRate).forEach(([rate, net]) => {
            netByRateAfterDiscount[rate] = baseNet > 0 ? net - discountAmount * (net / baseNet) : net;
        });

        let vatByRate = {};
        let vatAmount = 0;
        if (!profil?.kleinunternehmer && !isReverseCharge) { // Add !isReverseCharge condition
            Object.entries(netByRateAfterDiscount).forEach(([rate, net]) => {
                const v = net * (Number(rate) / 100);
                vatByRate[rate] = v;
                vatAmount += v;
            });
        }

        const totalGross = totalNet + vatAmount;

        return {
            netByRate,
            netByRateAfterDiscount,
            vatByRate,
            baseNet,
            totalNet,
            vatAmount,
            totalGross,
            discountAmount,
        };
    }, [invoiceArticles, totalDiscountType, totalDiscountValue, profil?.kleinunternehmer, isReverseCharge]);

    const handleRemoveInvoiceArticle = (idx) => {
        setInvoiceArticles((prev) =>
            normalizeInvoiceArticles(prev.filter((_, i) => i !== idx))
        );
        setArticleErrors((prev) => {
            const entries = Object.entries(prev);
            if (entries.length === 0) return prev;
            const next = {};
            entries.forEach(([key, value]) => {
                const index = Number(key);
                if (!Number.isFinite(index) || index === idx) return;
                next[index > idx ? index - 1 : index] = value;
            });
            return next;
        });
    };

    const openInvoicePreviewPdf = async (invoice) => {
        if (!invoice) return;
        const resolveCurrency = (value) => {
            const candidate = (value || profil?.baseCurrency || "EUR").toString().trim();
            return candidate ? candidate.toUpperCase() : "EUR";
        };
        const formatMoney = (value) => fmtCurrency(value, resolveCurrency(invoice.currency));
        const formatDate = (value) => {
            if (!value) return "-";
            try {
                return new Date(value).toLocaleDateString("de-DE");
            } catch {
                return value;
            }
        };

        const customer =
            invoice.customer ||
            customers.find((c) => c.id === invoice.customerId) ||
            null;
        const customerName =
            [
                customer?.companyName,
                [customer?.name, customer?.lastname].filter(Boolean).join(" "),
                invoice.customerName,
            ]
                .filter((v) => !!v && String(v).trim().length > 0)
                .map((v) => String(v).trim())
                .find(Boolean) || "Unbekannter Kunde";
        const customerAddress =
            customer?.addresses?.find((addr) => addr.forOutgoingInvoice) ||
            customer?.addresses?.[0] ||
            null;
        const customerAddressLine = customerAddress
            ? [
                customerAddress.street,
                `${customerAddress.zip || ""} ${customerAddress.city || ""}`.trim(),
            ]
                .filter(Boolean)
                .join(", ")
            : "";

        const performanceLocation = [invoice.street, invoice.zip, invoice.city]
            .filter(Boolean)
            .join(" ");

        const items = (invoice.articles || []).map((item, idx) => {
            const amount = toNumberSafe(item.amount);
            const unitPrice = toNumberSafe(item.unitPrice);
            const discountPct = toNumberSafe(item.articleDiscount);
            const taxRate = toNumberSafe(item.taxRate);
            const net = amount * unitPrice * (1 - (discountPct || 0) / 100);
            const vat = profil?.kleinunternehmer ? 0 : net * (taxRate / 100);
            const gross = net + vat;
            const title =
                item.description ||
                articles.find((a) => a.id === item.articleId)?.name ||
                `Position ${idx + 1}`;
            return {
                index: idx + 1,
                title,
                amount,
                unit: item.unit || "Stk",
                unitPrice,
                discountPct,
                discountReason: item.articleDiscountReason,
                taxRate,
                net,
                vat,
                gross,
            };
        });

        const resolvedFreeText =
            typeof invoice?.freeText === "string"
                ? invoice.freeText
                : invoice?.id
                    ? ""
                    : invoiceFreeText;

        const netByRate = items.reduce((acc, item) => {
            const key = String(item.taxRate || 0);
            acc[key] = (acc[key] || 0) + item.net;
            return acc;
        }, {});
        const baseNet = items.reduce((sum, item) => sum + item.net, 0);
        const discountValue = toNumberSafe(invoice.totalDiscountValue);
        let totalDiscountAmount = 0;
        if (invoice.totalDiscountType === "percent") {
            totalDiscountAmount = baseNet * (discountValue / 100);
        } else if (invoice.totalDiscountType === "fixed") {
            totalDiscountAmount = discountValue;
        }
        const netAfterDiscount = Math.max(baseNet - totalDiscountAmount, 0);

        const vatByRate = {};
        let vatSum = 0;
        if (!profil?.kleinunternehmer) {
            Object.entries(netByRate).forEach(([rate, net]) => {
                const share = baseNet > 0 ? net / baseNet : 0;
                const netAfterRate = netAfterDiscount * share;
                const vat = netAfterRate * (toNumberSafe(rate) / 100);
                vatByRate[rate] = vat;
                vatSum += vat;
            });
        }

        const resolvedNet = invoice.totalNet != null ? toNumberSafe(invoice.totalNet) : netAfterDiscount;
        const resolvedVat = invoice.totalVat != null ? toNumberSafe(invoice.totalVat) : vatSum;
        const resolvedGross =
            invoice.totalGross != null ? toNumberSafe(invoice.totalGross) : resolvedNet + resolvedVat;

        const typeLabel = invoice.invoiceType ? invoiceTypeLabels[invoice.invoiceType] || invoice.invoiceType : "";

        const loadLogoForPdf = async () => {
            if (!logoUrl) return null;
            try {
                const response = await fetch(logoUrl);
                const blob = await response.blob();
                const objectUrl = URL.createObjectURL(blob);
                try {
                    const image = await new Promise((resolve, reject) => {
                        const imgEl = new Image();
                        imgEl.onload = () => resolve(imgEl);
                        imgEl.onerror = reject;
                        imgEl.src = objectUrl;
                    });

                    const canvas = document.createElement("canvas");
                    canvas.width = image.naturalWidth || image.width || 1;
                    canvas.height = image.naturalHeight || image.height || 1;
                    const ctx = canvas.getContext("2d");
                    ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
                    const dataUrl = canvas.toDataURL("image/jpeg", 0.92);
                    const base64 = dataUrl.split(",")[1];
                    const binary = atob(base64);
                    const bytes = new Uint8Array(binary.length);
                    for (let i = 0; i < binary.length; i += 1) {
                        bytes[i] = binary.charCodeAt(i);
                    }
                    return { bytes, width: canvas.width, height: canvas.height };
                } finally {
                    URL.revokeObjectURL(objectUrl);
                }
            } catch (err) {
                logError("Logo konnte nicht für PDF geladen werden", err, { component: "InvoiceManagement" });
                return null;
            }
        };

        const winAnsiMap = {
            0x20ac: 0x80,
            0x201a: 0x82,
            0x0192: 0x83,
            0x201e: 0x84,
            0x2026: 0x85,
            0x2020: 0x86,
            0x2021: 0x87,
            0x02c6: 0x88,
            0x2030: 0x89,
            0x0160: 0x8a,
            0x2039: 0x8b,
            0x0152: 0x8c,
            0x017d: 0x8e,
            0x2018: 0x91,
            0x2019: 0x92,
            0x201c: 0x93,
            0x201d: 0x94,
            0x2022: 0x95,
            0x2013: 0x96,
            0x2014: 0x97,
            0x02dc: 0x98,
            0x2122: 0x99,
            0x0161: 0x9a,
            0x203a: 0x9b,
            0x0153: 0x9c,
            0x017e: 0x9e,
            0x0178: 0x9f,
        };

        const encodeWinAnsi = (text) => {
            const bytes = [];
            for (const char of text || "") {
                const codePoint = char.codePointAt(0);
                if (codePoint <= 0xff) {
                    bytes.push(codePoint);
                } else if (winAnsiMap[codePoint] !== undefined) {
                    bytes.push(winAnsiMap[codePoint]);
                } else {
                    bytes.push(0x3f); // Fallback to "?"
                }
            }
            return new Uint8Array(bytes);
        };
        const byteLengthWinAnsi = (text) => encodeWinAnsi(text).length;
        const toBinaryString = (uint8) => {
            if (!uint8) return "";
            const chunkSize = 0x8000;
            let result = "";
            for (let i = 0; i < uint8.length; i += chunkSize) {
                result += String.fromCharCode(...uint8.subarray(i, i + chunkSize));
            }
            return result;
        };

        const escapePdf = (str) =>
            (str || "")
                .toString()
                .replace(/\\/g, "\\\\")
                .replace(/\(/g, "\\(")
                .replace(/\)/g, "\\)");
        const lines = [];
        let cursorY = 800;
        const pushLine = (text, fontSize = 11) => {
            if (text === null || text === undefined) return;
            lines.push({ text: String(text), fontSize, y: cursorY });
            cursorY -= Math.max(fontSize + 4, 14);
        };

        const pageWidth = 595;
        const pageHeight = 842;
        const logoData = await loadLogoForPdf();
        let logoDrawCommand = "";
        let logoObject = null;
        if (logoData) {
            const maxLogoWidth = 160;
            const maxLogoHeight = 60;
            const scale = Math.min(
                maxLogoWidth / Math.max(logoData.width, 1),
                maxLogoHeight / Math.max(logoData.height, 1),
                1
            );
            const logoWidth = Math.max(1, logoData.width * scale);
            const logoHeight = Math.max(1, logoData.height * scale);
            const margin = 40;
            const logoX = pageWidth - margin - logoWidth;
            const logoY = pageHeight - margin - logoHeight;
            logoDrawCommand = `q ${logoWidth.toFixed(2)} 0 0 ${logoHeight.toFixed(2)} ${logoX.toFixed(2)} ${logoY.toFixed(2)} cm /Im1 Do Q`;
            logoObject = `6 0 obj << /Type /XObject /Subtype /Image /Width ${logoData.width} /Height ${logoData.height} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${logoData.bytes.length} >> stream\n${toBinaryString(logoData.bytes)}\nendstream endobj\n`;
        }

        pushLine(`${documentLabel} ${invoice.invoiceNumber || "ohne Nummer"}`, 16);
        if (typeLabel) pushLine(`Typ: ${typeLabel}`, 12);
        pushLine(customerName, 12);
        if (customerAddressLine) pushLine(customerAddressLine);
        if (performanceLocation) pushLine(`Leistungsort: ${performanceLocation}`);
        
        if (invoice.isReverseCharge) {
            pushLine("");
            pushLine("Hinweis: Steuerschuldnerschaft des Leistungsempfängers (Reverse-Charge-Verfahren)");
            if (customer?.taxData?.ustId) {
                pushLine(`USt-IdNr. Leistungsempfänger: ${customer.taxData.ustId}`);
            } else if (customer?.taxData?.vatNumber) {
                pushLine(`USt-IdNr. Leistungsempfänger: ${customer.taxData.vatNumber}`);
            }
        }

        pushLine("");
        pushLine(`${documentDateLabel}: ${formatDate(invoice.invoiceDate)}   ${documentDueLabel}: ${formatDate(invoice.dueDate)}`);
        if (invoice.performanceDate) pushLine(`Leistungsdatum: ${formatDate(invoice.performanceDate)}`);
        if (invoice.baseAmount != null || invoice.remainingAmount != null) {
            const pieces = [];
            if (invoice.baseAmount != null) pieces.push(`Basis ${formatMoney(toNumberSafe(invoice.baseAmount))}`);
            if (invoice.remainingAmount != null) {
                pieces.push(`Rest ${formatMoney(toNumberSafe(invoice.remainingAmount))}`);
            }
            if (pieces.length > 0) {
                pushLine(pieces.join(" | "));
            }
        }

        pushLine("");
        pushLine("Positionen", 13);
        if (items.length === 0) {
            pushLine("Keine Positionen vorhanden.");
        } else {
            items.forEach((item) => {
                pushLine(`${item.index}. ${item.title}`);
                const metaParts = [
                    `${item.amount} ${item.unit}`,
                    `x ${formatMoney(item.unitPrice)}`,
                ];
                if (item.discountPct) {
                    metaParts.push(`Rabatt ${item.discountPct}%`);
                }
                metaParts.push(`Netto ${formatMoney(item.net)}`);
                if (!profil?.kleinunternehmer) {
                    metaParts.push(`USt ${item.taxRate}%: ${formatMoney(item.vat)}`);
                }
                metaParts.push(`Brutto ${formatMoney(item.gross)}`);
                pushLine(`   ${metaParts.filter(Boolean).join(" | ")}`);
                if (item.discountReason) {
                    pushLine(`   Rabattgrund: ${item.discountReason}`);
                }
            });
        }

        const trimmedFreeText = resolvedFreeText.trim();
        if (trimmedFreeText) {
            pushLine("");
            trimmedFreeText.split(/\r?\n/).forEach((line) => pushLine(line));
        }

        pushLine("");
        pushLine("Summen", 13);
        if (totalDiscountAmount > 0 && invoice.totalDiscountType) {
            const discountLabel =
                invoice.totalDiscountType === "percent"
                    ? `${formatMoney(totalDiscountAmount)} (${discountValue}% Rabatt)`
                    : formatMoney(totalDiscountAmount);
            pushLine(`Gesamtrabatt: ${discountLabel}`);
        }
        pushLine(`Netto: ${formatMoney(resolvedNet)}`);
        if (!profil?.kleinunternehmer) {
            Object.entries(vatByRate).forEach(([rate, amount]) => {
                pushLine(`USt ${rate}%: ${formatMoney(amount)}`);
            });
            pushLine(`Brutto: ${formatMoney(resolvedGross)}`);
        } else {
            pushLine("Hinweis: Umsatzsteuer wird nicht ausgewiesen (Kleinunternehmerregelung).");
            pushLine(`Gesamtbetrag: ${formatMoney(resolvedGross)}`);
        }
        pushLine("");
        pushLine("Hinweis: Lokale Fallback-Vorschau (Server-PDF nicht verfuegbar).");

        const contentBody = lines
            .map(({ text, fontSize, y }) => `BT /F1 ${fontSize} Tf 50 ${y} Td (${escapePdf(text)}) Tj ET`)
            .join("\n");
        const content = [logoDrawCommand, contentBody].filter(Boolean).join("\n");
        const contentLength = byteLengthWinAnsi(content);

        const objects = [
            `1 0 obj << /Type /Catalog /Pages 2 0 R >> endobj\n`,
            `2 0 obj << /Type /Pages /Count 1 /Kids [3 0 R] >> endobj\n`,
            `3 0 obj << /Type /Page /Parent 2 0 R /MediaBox [0 0 ${pageWidth} ${pageHeight}] /Resources << /Font << /F1 5 0 R >>${logoObject ? ` /XObject << /Im1 6 0 R >>` : ""} >> /Contents 4 0 R >> endobj\n`,
            `4 0 obj << /Length ${contentLength} >> stream\n${content}\nendstream endobj\n`,
            `5 0 obj << /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >> endobj\n`,
            ...(logoObject ? [logoObject] : []),
        ];

        const header = "%PDF-1.4\n";
        const offsets = [0];
        const bodyParts = [header];
        let cursor = byteLengthWinAnsi(header);
        objects.forEach((obj) => {
            offsets.push(cursor);
            bodyParts.push(obj);
            cursor += byteLengthWinAnsi(obj);
        });

        const xrefStart = cursor;
        const xrefEntries = offsets
            .map((offset, idx) => `${offset.toString().padStart(10, "0")} ${idx === 0 ? "65535 f " : "00000 n "}`)
            .join("\n");
        const xref = `xref\n0 ${objects.length + 1}\n${xrefEntries}\ntrailer << /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xrefStart}\n%%EOF`;
        bodyParts.push(xref);

        const pdf = bodyParts.join("");
        const pdfBytes = encodeWinAnsi(pdf);
        const blob = new Blob([pdfBytes], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        window.open(url, "_blank", "noopener");
        setTimeout(() => window.URL.revokeObjectURL(url), 30_000);
    };
    const handleViewPdf = async (invoice) => {
        if (!invoice || typeof invoice !== "object") return;
        if (!invoice.id) {
            openInvoicePreviewPdf(invoice)
                .then(() => showInfo("Lokale Vorschau geoeffnet (Server-PDF nicht verfuegbar)."))
                .catch((err) => showError("Vorschau konnte nicht geoeffnet werden", err));
            return;
        }

        try {
            const result = await downloadInvoicePdf(invoice.id, { suppressAuthError: true });
            const blob = result?.data;
            if (!(blob instanceof Blob)) {
                throw new Error("Kein PDF erhalten");
            }
            const url = window.URL.createObjectURL(blob);
            const opened = window.open(url, "_blank", "noopener");
            if (!opened) {
                const link = document.createElement("a");
                link.href = url;
                link.download = result.fileName || `${invoice.invoiceNumber || invoice.id}.pdf`;
                document.body.appendChild(link);
                link.click();
                link.remove();
            }
            setTimeout(() => window.URL.revokeObjectURL(url), 30_000);
            showInfo("Server-PDF geoeffnet");
        } catch (err) {
            showError("Server-PDF konnte nicht geladen werden. Fallback wird angezeigt.", err);
            openInvoicePreviewPdf(invoice).catch((fallbackErr) =>
                showError("Vorschau konnte nicht geoeffnet werden", fallbackErr)
            );
        }
    };

    const handleViewXRechnung = async (invoice) => {
        if (!invoice || typeof invoice !== "object") return;
        if (!invoice.id) {
            showError("XRechnung ist erst nach dem Speichern verfuegbar.");
            return;
        }

        try {
            const result = await downloadInvoiceXRechnung(invoice.id);
            const blob = result?.data;
            if (!(blob instanceof Blob)) {
                throw new Error("Keine XRechnung erhalten");
            }
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = result.fileName || `${invoice.invoiceNumber || invoice.id}-xrechnung.xml`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            setTimeout(() => window.URL.revokeObjectURL(url), 30_000);
            showInfo("XRechnung heruntergeladen");
        } catch (err) {
            showError("XRechnung konnte nicht geladen werden. Bitte pruefen, ob sie erstellt wurde.", err);
        }
    };

    const renderPreviewCard = (allowRemoval = true) => (
        <InvoicePreviewCard
            allowRemoval={allowRemoval}
            invoiceType={invoiceType}
            parentInvoice={parentInvoice}
            parentMetrics={parentMetrics}
            documentNumberLabel={documentNumberLabel}
            invoiceNextNumber={invoiceNextNumber}
            logoUrl={logoUrl}
            selectedCustomer={selectedCustomer}
            selectedLocation={selectedLocation}
            documentDateLabel={documentDateLabel}
            documentDueLabel={documentDueLabel}
            invoiceDate={invoiceDate}
            dueDate={dueDate}
            performanceDate={performanceDate}
            invoiceArticles={invoiceArticles}
            profil={profil}
            totalDiscountType={totalDiscountType}
            totals={totals}
            invoiceFreeText={invoiceFreeText}
            fieldErrors={fieldErrors}
            getArticleErrorSummary={getArticleErrorSummary}
            handleRemoveInvoiceArticle={handleRemoveInvoiceArticle}
        />
    );

    // eslint-disable-next-line no-unused-vars
    const renderTypeStep = () => (
        <InvoiceTypeStep
            invoiceType={invoiceType}
            setInvoiceType={setInvoiceType}
            clearFieldError={clearFieldError}
            parentInvoiceId={parentInvoiceId}
            setParentInvoiceId={setParentInvoiceId}
            parentInvoiceOptions={parentInvoiceOptions}
            fieldErrors={fieldErrors}
            baseAmountOverride={baseAmountOverride}
            setBaseAmountOverride={setBaseAmountOverride}
            partPercent={partPercent}
            setPartPercent={setPartPercent}
            projectedPartAmount={projectedPartAmount}
            parentInvoice={parentInvoice}
            parentMetrics={parentMetrics}
            documentLabel={documentLabel}
        />
    );

    // eslint-disable-next-line no-unused-vars
    const renderDetailStep = () => (
        <Grid2 container spacing={3}>
            <Grid2 xs={12} md={4}>
                <Card sx={{ borderRadius: 3, boxShadow: "0 6px 24px rgba(0,0,0,0.06)" }}>
                    <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Chip
                                size="small"
                                color={invoiceTypeChipColor[invoiceType] || "default"}
                                variant={invoiceType === "STANDARD" ? "outlined" : "filled"}
                                label={`Typ: ${invoiceTypeLabels[invoiceType] || invoiceType}`}
                            />
                            {parentInvoice && (
                                <Typography variant="body2" color="text.secondary">
                                    Bezug: {parentInvoice.invoiceNumber || parentInvoice.id?.slice(0, 8)}
                                </Typography>
                            )}
                        </Stack>
                        {parentInvoice && (
                            <Typography variant="body2" color="text.secondary">
                                Restbetrag: {fmtEUR(parentMetrics.remaining ?? 0)}
                            </Typography>
                        )}

                        <Autocomplete
                            options={customerOptions}
                            loading={customersLoading}
                            value={selectedCustomerOption}
                            onChange={(_, value) => {
                                setSelectedCustomerId(value?.id || "");
                                clearFieldError("customerId");
                            }}
                            getOptionLabel={getCustomerOptionLabel}
                            isOptionEqualToValue={isCustomerOptionEqualToValue}
                            loadingText={CUSTOMER_SELECT_LOADING}
                            noOptionsText={CUSTOMER_SELECT_NO_OPTIONS}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={CUSTOMER_SELECT_LABEL}
                                    placeholder={CUSTOMER_SELECT_PLACEHOLDER}
                                    error={Boolean(fieldErrors.customerId)}
                                    helperText={
                                        fieldErrors.customerId ||
                                        (!customersLoading && customerOptions.length === 0
                                            ? CUSTOMER_SELECT_EMPTY_HELPER
                                            : "")
                                    }
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {customersLoading ? (
                                                    <CircularProgress color="inherit" size={20} />
                                                ) : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                            fullWidth
                        />

                        <TextField
                            select
                            label="Leistungsort/Adresse auswählen"
                            value={selectedLocation}
                            onChange={(e) => {
                                setSelectedLocation(e.target.value);
                                clearFieldError("performanceLocation");
                            }}
                            error={Boolean(fieldErrors.performanceLocation)}
                            helperText={fieldErrors.performanceLocation || ""}
                            fullWidth
                        >
                            {(selectedCustomer?.addresses || []).map((address) => (
                                <MenuItem key={address.id || `${address.street}-${address.zip}`} value={address}>
                                    {address.street}, {address.zip} {address.city}
                                </MenuItem>
                            ))}
                        </TextField>

                        <Divider sx={{ my: 1 }} />

                        <AppDatePicker
                            label={documentDateLabel}
                            value={invoiceDate}
                            onChange={(date) => {
                                setInvoiceDate(date);
                                clearFieldError("invoiceDate");
                                generateInvoiceNumber();
                            }}
                            error={Boolean(fieldErrors.invoiceDate)}
                            helperText={fieldErrors.invoiceDate || ""}
                            fullWidth
                        />
                        <AppDatePicker
                            label={documentDueLabel}
                            value={dueDate}
                            onChange={(date) => {
                                setDueDate(date);
                                clearFieldError("dueDate");
                            }}
                            error={Boolean(fieldErrors.dueDate)}
                            helperText={fieldErrors.dueDate || ""}
                            fullWidth
                        />
                        <AppDatePicker
                            label="Leistungsdatum"
                            value={performanceDate}
                            onChange={(date) => {
                                setPerformanceDate(date);
                                clearFieldError("performanceDate");
                            }}
                            error={Boolean(fieldErrors.performanceDate)}
                            helperText={fieldErrors.performanceDate || ""}
                            fullWidth
                        />

                        <Divider sx={{ my: 1 }} />

                        <ToggleButtonGroup
                            color="primary"
                            value={isFreeText ? "manual" : "article"}
                            exclusive
                            onChange={(_, val) => val && setIsFreeText(val === "manual")}
                            fullWidth
                            size="small"
                            sx={{ mb: 1 }}
                        >
                            <ToggleButton value="article">Artikel wählen</ToggleButton>
                            <ToggleButton value="manual">Manueller Eintrag</ToggleButton>
                        </ToggleButtonGroup>

                        {!isFreeText ? (
                            <TextField
                                select
                                label="Artikel auswählen"
                                value={selectedArticleId}
                                onChange={(e) => setSelectedArticleId(e.target.value)}
                                fullWidth
                            >
                                {sortedArticles.map((article) => {
                                    const net = article.price ?? 0;
                                    const gross = net * (1 + (article.taxRate || 0) / 100);
                                    const label = profil?.kleinunternehmer
                                        ? `${article.name} – ${net.toFixed(2)} €`
                                        : `${article.name} – ${net.toFixed(2)} € netto (${gross.toFixed(2)} € brutto)`;
                                    return (
                                        <MenuItem key={article.id} value={article.id}>
                                            {label}
                                        </MenuItem>
                                    );
                                })}
                            </TextField>
                        ) : (
                            <Stack spacing={2}>
                                <TextField
                                    label="Bezeichnung"
                                    value={freeTextName}
                                    onChange={(e) => setFreeTextName(e.target.value)}
                                    fullWidth
                                />
                                <Grid2 container spacing={1}>
                                    <Grid2 size={6}>
                                        <TextField
                                            label="Preis (€ netto)"
                                            type="text"
                                            value={freeTextPrice}
                                            onChange={(e) => setFreeTextPrice(e.target.value)}
                                            inputMode="decimal"
                                            inputProps={{ inputMode: "decimal", pattern: "[0-9]*[.,]?[0-9]*" }}
                                            fullWidth
                                        />
                                    </Grid2>
                                    <Grid2 size={6}>
                                        <TextField
                                            label="USt (%)"
                                            type="text"
                                            value={freeTextTaxRate}
                                            onChange={(e) => setFreeTextTaxRate(e.target.value)}
                                            inputMode="decimal"
                                            inputProps={{ inputMode: "decimal", pattern: "[0-9]*[.,]?[0-9]*" }}
                                            fullWidth
                                        />
                                    </Grid2>
                                </Grid2>
                                <TextField
                                    label="Einheit"
                                    placeholder="Stk, h, m², ..."
                                    value={freeTextUnit}
                                    onChange={(e) => setFreeTextUnit(e.target.value)}
                                    fullWidth
                                />
                            </Stack>
                        )}

                        {(() => {
                            const parsed = parsePositiveDecimalInput(selectedAmount);
                            const amountErrorText = parsed.error === "invalid"
                                ? "Bitte eine gueltige Zahl eingeben (z.B. 1,5)."
                                : parsed.error === "too_small"
                                    ? "Menge muss mindestens 0,01 sein."
                                    : "";

                            const isAmountValid = Boolean(parsed.value) && !parsed.error;

                            const isAddDisabled = isFreeText
                                ? (!freeTextName || freeTextPrice === "" || !isAmountValid)
                                : (!selectedArticleId || !isAmountValid);

                            return (
                                <>
                                    <TextField
                                    label="Menge"
                                    value={selectedAmount}
                            onChange={(e) => setSelectedAmount(e.target.value)}
                                    // Use text + decimal input mode so users can enter "1,5" as well as "1.5".
                                    type="text"
                                    inputMode="decimal"
                                    inputProps={{ inputMode: "decimal", pattern: "[0-9]*[.,]?[0-9]*" }}
                            error={Boolean(amountErrorText)}
                            helperText={amountErrorText}
                                    fullWidth
                        />

                        <FormControl component="fieldset">
                            <FormLabel component="legend">Rabatt auf Artikel?</FormLabel>
                            <RadioGroup
                                row
                                value={useArticleDiscount ? "yes" : "no"}
                                onChange={(e) => setArticleDiscount(e.target.value === "yes")}
                            >
                                <FormControlLabel value="yes" control={<Radio />} label="Ja" />
                                <FormControlLabel value="no" control={<Radio />} label="Nein" />
                            </RadioGroup>
                        </FormControl>

                        {useArticleDiscount && (
                            <>
    <TextField
        label="Artikel-Rabatt in %"
        value={articleDiscountPercent}
        onChange={(e) => setArticleDiscountPercent(e.target.value)}
        fullWidth
        margin="dense"
        size="small"
        type="number"
        inputMode="decimal"
        inputProps={{ step: "0.01", min: "0" }}
    />
                                <TextField
                                    label="Grund für Artikelrabatt"
                                    value={articleDiscountReason}
                                    onChange={(e) => setArticleDiscountReason(e.target.value)}
                                    fullWidth
                                />
                            </>
                        )}

                        <Button
                            variant="contained"
                            onClick={handleAddArticle}
                            sx={{ bgcolor: "orange", "&:hover": { bgcolor: "#cc7a00" }, borderRadius: 2, color: "#000" }}
                            disabled={isAddDisabled}
                        >
                            Artikel hinzufügen
                        </Button>
                                </>
                            );
                        })()}

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={hasShippingCost}
                                    onChange={(e) => handleToggleShippingCost(e.target.checked)}
                                    disabled={sortedShippingCosts.length === 0}
                                />
                            }
                            label="Versandkosten hinzufügen"
                        />

                        <TextField
                            select
                            label="Versandkosten auswählen"
                            value={selectedShippingCostId}
                            onChange={(e) => handleShippingCostSelectionChange(e.target.value)}
                            fullWidth
                        >
                            {sortedShippingCosts.length === 0 ? (
                                <MenuItem value="" disabled>
                                    Keine Versandkosten vorhanden
                                </MenuItem>
                            ) : (
                                <MenuItem value="">Keine Versandkosten</MenuItem>
                            )}
                            {sortedShippingCosts.map((cost) => {
                                const net = cost.price ?? 0;
                                const gross = net * (1 + (cost.taxRate || 0) / 100);
                                const label = profil?.kleinunternehmer
                                    ? `${cost.name} - ${net.toFixed(2)} €`
                                    : `${cost.name} - ${net.toFixed(2)} € netto (${gross.toFixed(2)} € brutto)`;
                                return (
                                    <MenuItem key={cost.id} value={cost.id}>
                                        {label}
                                    </MenuItem>
                                );
                            })}
                        </TextField>

                        <Divider sx={{ my: 1 }} />

                        <FormControl component="fieldset" error={Boolean(fieldErrors.totalDiscountType)}>
                            <FormLabel component="legend">Gesamtrabatt</FormLabel>
                            <RadioGroup
                                row
                                value={totalDiscountType}
                                onChange={(e) => {
                                    setTotalDiscountType(e.target.value);
                                    clearFieldError("totalDiscountType");
                                }}
                            >
                                <FormControlLabel value="none" control={<Radio />} label="Kein Rabatt" />
                                <FormControlLabel value="percent" control={<Radio />} label="Prozentual" />
                                <FormControlLabel value="fixed" control={<Radio />} label="Fester Betrag" />
                            </RadioGroup>
                            {fieldErrors.totalDiscountType && (
                                <FormHelperText>{fieldErrors.totalDiscountType}</FormHelperText>
                            )}
                        </FormControl>

                        {totalDiscountType !== "none" && (
    <TextField
        label={
            totalDiscountType === "percent"
                ? "Rabatt in %"
                : "Rabatt in EUR"
        }
        value={totalDiscountValue}
        onChange={(e) => setTotalDiscountValue(e.target.value)}
        type="number"
        inputMode="decimal"
        inputProps={{ step: "0.01", min: "0" }}
        fullWidth
        margin="normal"
        disabled={totalDiscountType === "none"}
        error={!!fieldErrors.totalDiscountValue}
        helperText={fieldErrors.totalDiscountValue}
    />
                        )}

                    </CardContent>
                </Card>
            </Grid2>
            <Grid2 xs={12} md={8} sx={{ position: "sticky", top: 96, alignSelf: "flex-start" }}>
                {renderPreviewCard(true)}
            </Grid2>
        </Grid2>
    );

    // eslint-disable-next-line no-unused-vars
    const renderReviewStep = () => {
        const baseForSummary = parentMetrics.base ?? totals.totalGross;
        const partValue = projectedPartAmount != null ? projectedPartAmount : null;
        return (
            <Grid2 container spacing={3}>
                <Grid2 xs={12} md={4}>
                    <Card sx={{ borderRadius: 3, boxShadow: "0 6px 24px rgba(0,0,0,0.06)" }}>
                        <CardContent>
                            <Typography variant="h6" sx={{ mb: 1 }}>
                                Zusammenfassung
                            </Typography>
                            <List dense>
                                <ListItem>
                                    <ListItemText
                                        primary="Rechnungstyp"
                                        secondary={invoiceTypeLabels[invoiceType] || invoiceType}
                                    />
                                </ListItem>
                                {parentInvoice && (
                                    <ListItem>
                                        <ListItemText
                                            primary="Bezugsrechnung"
                                            secondary={`${parentInvoice.invoiceNumber || parentInvoice.id?.slice(0, 8)} · Rest ${fmtEUR(parentMetrics.remaining ?? 0)}`}
                                        />
                                    </ListItem>
                                )}
                                <ListItem>
                                    <ListItemText
                                        primary="Projektbasis"
                                        secondary={fmtEUR(baseForSummary ?? 0)}
                                    />
                                </ListItem>
                                {partValue != null && (
                                    <ListItem>
                                        <ListItemText
                                            primary="Anteil"
                                            secondary={`${partPercent || 0}% (${fmtEUR(partValue)})`}
                                        />
                                    </ListItem>
                                )}
                                <ListItem>
                                    <ListItemText
                                        primary="Positionen"
                                        secondary={`${invoiceArticles.length}`}
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemText
                                        primary="Bruttosumme"
                                        secondary={fmtEUR(totals.totalGross)}
                                    />
                                </ListItem>
                            </List>
                        </CardContent>
                    </Card>
                </Grid2>
                <Grid2 xs={12} md={8} sx={{ position: "sticky", top: 96, alignSelf: "flex-start" }}>
                    {renderPreviewCard(false)}
                </Grid2>
            </Grid2>
        );
    };

    /* ===== Render ===== */
    return (
        <>
            {/* Full-bleed Hero */}
            <Box
                sx={{
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    background: "linear-gradient(135deg, rgba(255,165,0,0.12) 0%, rgba(0,0,0,0.06) 100%)",
                    py: { xs: 3, md: 4 },
                }}
            >
                <Container maxWidth="lg">
                    <Stack
                        direction={{ xs: "column", md: "row" }}
                        spacing={2}
                        alignItems={{ xs: "flex-start", md: "center" }}
                        justifyContent="space-between"
                    >
                        <Box>
                            <Typography variant="h4" sx={{ fontWeight: 900, letterSpacing: 0.2 }}>
                                {documentLabelPlural}
                            </Typography>
                            <Typography variant="subtitle1" sx={{ mt: 0.5 }}>
                                {documentSubtitle}
                            </Typography>
                        </Box>
                        <Stack direction="row" spacing={1}>
                            <Button
                                variant="outlined"
                                startIcon={<RefreshRoundedIcon />}
                                onClick={() => {
                                    refreshInvoices();
                                    setSnack({ open: true, msg: "Aktualisiert.", sev: "success" });
                                }}
                            >
                                Aktualisieren
                            </Button>
                            <Button
                                variant="contained"
                                sx={{
                                    backgroundColor: "orange",
                                    color: "#000",
                                    fontWeight: 800,
                                    "&:hover": { backgroundColor: "#ff9e1a" },
                                }}
                                onClick={() => {
                                    resetForm();
                                    setOpenDialog(true);
                                }}
                            >
                                {createDocumentLabel}
                            </Button>
                        </Stack>
                    </Stack>
                </Container>
            </Box>

            <Container maxWidth="lg" sx={{ mt: 4, pb: 6 }}>
                <InvoiceList
                    isOffer={isOffer}
                    isInvoiceLoading={isInvoiceLoading}
                    invoiceTree={invoiceTree}
                    documentLabel={documentLabel}
                    documentLabelPlural={documentLabelPlural}
                    payLinkFilter={payLinkFilter}
                    setPayLinkFilter={setPayLinkFilter}
                    showEmptyState={showEmptyState}
                    hasInvoices={hasInvoices}
                    groupedInvoices={groupedInvoices}
                    sortedYears={sortedYears}
                    expandedInvoiceGroups={expandedInvoiceGroups}
                    toggleYearExpanded={toggleYearExpanded}
                    toggleMonthExpanded={toggleMonthExpanded}
                    loadingInvoiceMonths={loadingInvoiceMonths}
                    loadedInvoiceMonths={loadedInvoiceMonths}
                    cancelledInvoiceIds={cancelledInvoiceIds}
                    handleConvertOfferToInvoice={handleConvertOfferToInvoice}
                    handleViewPdf={handleViewPdf}
                    handlePaidChange={handlePaidChange}
                    handleSentChange={handleSentChange}
                    handleOpenPartialPayment={handleOpenPartialPayment}
                    handleOpenCancelDialog={handleOpenCancelDialog}
                    handleOpenWorkOrderDialog={handleOpenWorkOrderDialog}
                    handleCreatePayLink={handleCreatePayLink}
                    handleCopyPayLink={handleCopyPayLink}
                    handleViewXRechnung={handleViewXRechnung}
                    resolveInvoiceCustomerName={resolveInvoiceCustomerName}
                />
            </Container>

            {/* Rechnung erstellen */}
            <Dialog
                open={openDialog}
                onClose={handleCloseCreateDialog}
                maxWidth="lg"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
                aria-labelledby="create-document-dialog-title"
            >
                <DialogTitle id="create-document-dialog-title">
                    {isOffer ? "Neues Angebot erstellen" : "Neue Rechnung erstellen"}
                </DialogTitle>
                <DialogContent
                    dividers
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        height: "calc(100vh - 120px)",
                        p: 0,           // optional: damit Grid bündig sitzt
                    }}
                >
                    <Grid2
                        container
                        spacing={3}
                        sx={{
                            flex: 1,
                            minHeight: 0,  // 🔥 wichtig: erlaubt Kindern zu scrollen
                            height: "100%",
                            alignItems: "stretch",
                            p: 3,          // wenn du p:0 oben setzt, gib dem Grid hier Padding
                        }}
                    >
                        {/* Formular links */}
                        <Grid2
                            xs={12}
                            md={4}
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                minHeight: 0,      // 🔥 wichtig
                                height: "100%",
                            }}
                        >
                            <Card
                                sx={{
                                    borderRadius: 3,
                                    boxShadow: "0 6px 24px rgba(0,0,0,0.06)",
                                    display: "flex",
                                    flexDirection: "column",
                                    flex: 1,
                                    minHeight: 0,
                                }}
                            >
                                <CardContent
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 2,
                                        flex: 1,
                                        minHeight: 0,
                                        overflowY: "auto",
                                    }}
                                >
                                    {/* Kunde */}
                                    <Autocomplete
                                        options={customerOptions}
                                        loading={customersLoading}
                                        value={selectedCustomerOption}
                                        onChange={(_, value) => {
                                            setSelectedCustomerId(value?.id || "");
                                            clearFieldError("customerId");
                                        }}
                                        getOptionLabel={getCustomerOptionLabel}
                                        isOptionEqualToValue={isCustomerOptionEqualToValue}
                                        loadingText={CUSTOMER_SELECT_LOADING}
                                        noOptionsText={CUSTOMER_SELECT_NO_OPTIONS}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label={CUSTOMER_SELECT_LABEL}
                                                placeholder={CUSTOMER_SELECT_PLACEHOLDER}
                                                error={Boolean(fieldErrors.customerId)}
                                                helperText={
                                                    fieldErrors.customerId ||
                                                    (!customersLoading && customerOptions.length === 0
                                                        ? CUSTOMER_SELECT_EMPTY_HELPER
                                                        : "")
                                                }
                                                InputProps={{
                                                    ...params.InputProps,
                                                    endAdornment: (
                                                        <>
                                                            {customersLoading ? (
                                                                <CircularProgress color="inherit" size={20} />
                                                            ) : null}
                                                            {params.InputProps.endAdornment}
                                                        </>
                                                    ),
                                                }}
                                            />
                                        )}
                                        fullWidth
                                    />

                                    <TextField
                                        select
                                        label="Auftrag"
                                        value={selectedWorkOrderId}
                                        onChange={(e) => {
                                            setSelectedWorkOrderId(e.target.value);
                                            clearFieldError("workOrderId");
                                        }}
                                        helperText={
                                            fieldErrors.workOrderId
                                                ? fieldErrors.workOrderId
                                                : selectedCustomerId
                                                    ? "Optional"
                                                    : "Bitte zuerst Kunde auswaehlen."
                                        }
                                        disabled={!selectedCustomerId}
                                        fullWidth
                                        error={Boolean(fieldErrors.workOrderId)}
                                    >
                                        <MenuItem value="">Kein Auftrag</MenuItem>
                                        {selectedCustomerId && availableWorkOrders.length === 0 && (
                                            <MenuItem value="" disabled>
                                                Keine Auftraege vorhanden
                                            </MenuItem>
                                        )}
                                        {availableWorkOrders.map((order) => (
                                            <MenuItem key={order.id} value={order.id}>
                                                {order.title || order.id?.slice(0, 8)}
                                            </MenuItem>
                                        ))}
                                    </TextField>

                                    {/* Leistungsort */}
                                    <TextField
                                        select
                                        label="Leistungsort/Adresse auswählen"
                                        value={selectedLocation}
                                        onChange={(e) => {
                                            setSelectedLocation(e.target.value);
                                            clearFieldError("performanceLocation");
                                        }}
                                        error={Boolean(fieldErrors.performanceLocation)}
                                        helperText={fieldErrors.performanceLocation || ""}
                                        fullWidth
                                    >
                                        {(selectedCustomer?.addresses || []).map((address) => (
                                            <MenuItem key={address.id || `${address.street}-${address.zip}`} value={address}>
                                                {address.street}, {address.zip} {address.city}
                                            </MenuItem>
                                        ))}
                                    </TextField>

                                    <Divider sx={{ my: 1 }} />

                                    {/* Kopfdaten */}
                            <Grid2 xs={12} md={4}>
                                <AppDatePicker
                                    label="Rechnungsdatum"
                                    fullWidth
                                    value={invoiceDate || ""}
                                    onChange={(date) => setInvoiceDate(date)}
                                    error={Boolean(fieldErrors.invoiceDate)}
                                    helperText={fieldErrors.invoiceDate || ""}
                                />
                            </Grid2>
                            <Grid2 xs={12} md={4}>
                                <FormControl component="fieldset" margin="normal" error={Boolean(fieldErrors.dueDate)}>
                                    <FormLabel component="legend" focused={paymentTermDays !== null} sx={{ color: 'black' }}>Zahlungsziel</FormLabel>
                                    <RadioGroup
                                        row
                                        name="payment-term-options"
                                        value={paymentTermDays !== null ? String(paymentTermDays) : "manual"}
                                        onChange={(e) => {
                                            if (e.target.value === "manual") {
                                                setPaymentTermDays(null);
                                            } else {
                                                setPaymentTermDays(Number(e.target.value));
                                            }
                                        }}
                                    >
                                        {PAYMENT_TERM_OPTIONS.map((days) => (
                                            <FormControlLabel
                                                key={days}
                                                value={String(days)} // Ensure value is a string
                                                control={<Radio />}
                                                label={`${days} Tage`}
                                            />
                                        ))}
                                        <FormControlLabel
                                            value={"manual"} // Change to a string value
                                            control={<Radio />}
                                            label="Manuell"
                                            // onClick removed as onChange handles the logic now
                                        />
                                    </RadioGroup>
                                    <AppDatePicker
                                        label="Fälligkeitsdatum"
                                        fullWidth
                                        value={dueDate || ""}
                                        onChange={(date) => {
                                            setDueDate(date);
                                            setPaymentTermDays(null); // Set to null if manually changed
                                        }}
                                        helperText={fieldErrors.dueDate || ""}
                                        error={Boolean(fieldErrors.dueDate)}
                                        disabled={paymentTermDays !== null}
                                    />
                                    {fieldErrors.dueDate && <FormHelperText>{fieldErrors.dueDate}</FormHelperText>}
                                </FormControl>
                            </Grid2>
                            <Grid2 xs={12} md={4}>
                                <AppDatePicker
                                    label="Leistungsdatum"
                                    fullWidth
                                    value={performanceDate || ""}
                                    onChange={(date) => setPerformanceDate(date)}
                                    error={Boolean(fieldErrors.performanceDate)}
                                    helperText={fieldErrors.performanceDate || ""}
                                />
                            </Grid2>
                            <Grid2 xs={12}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={isReverseCharge}
                                            onChange={(e) => setIsReverseCharge(e.target.checked)}
                                            disabled={!hasCustomerVatId}
                                        />
                                    }
                                    label="Reverse Charge Verfahren anwenden"
                                />
                            </Grid2>

                                    {!isOffer && (
                                        <>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={createXRechnung}
                                                        onChange={(e) => setCreateXRechnung(e.target.checked)}
                                                    />
                                                }
                                                label="XRechnung erstellen"
                                            />
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={sendInvoiceEmailEnabled}
                                                        onChange={(e) => setSendInvoiceEmailEnabled(e.target.checked)}
                                                        disabled={!canSendInvoiceEmail}
                                                    />
                                                }
                                                label="Rechnung per E-Mail senden"
                                            />
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={isPaid}
                                                        onChange={(e) => setIsPaid(e.target.checked)}
                                                    />
                                                }
                                                label="bereits bezahlt"
                                            />
                                            {!canSendInvoiceEmail && selectedCustomerId && (
                                                <Typography variant="caption" color="text.secondary">
                                                    Kunde hat keine E-Mail-Adresse hinterlegt.
                                                </Typography>
                                            )}
                                        </>
                                    )}

                                    <Divider sx={{ my: 1 }} />

                                    <ToggleButtonGroup
                                        color="primary"
                                        value={isFreeText ? "manual" : "article"}
                                        exclusive
                                        onChange={(_, val) => val && setIsFreeText(val === "manual")}
                                        fullWidth
                                        size="small"
                                        sx={{ mb: 1 }}
                                    >
                                        <ToggleButton value="article">Artikel wählen</ToggleButton>
                                        <ToggleButton value="manual">Manueller Eintrag</ToggleButton>
                                    </ToggleButtonGroup>

                                    {!isFreeText ? (
                                        <TextField
                                            select
                                            label="Artikel auswählen"
                                            value={selectedArticleId}
                                            onChange={(e) => setSelectedArticleId(e.target.value)}
                                            fullWidth
                                        >
                                            {sortedArticles.map((article) => {
                                                const net = article.price ?? 0;
                                                const gross = net * (1 + (article.taxRate || 0) / 100);
                                                const label = profil?.kleinunternehmer
                                                    ? `${article.name} – ${net.toFixed(2)} €`
                                                    : `${article.name} – ${net.toFixed(2)} € netto (${gross.toFixed(2)} € brutto)`;
                                                return (
                                                    <MenuItem key={article.id} value={article.id}>
                                                        {label}
                                                    </MenuItem>
                                                );
                                            })}
                                        </TextField>
                                    ) : (
                                        <Stack spacing={2}>
                                            <TextField
                                                label="Bezeichnung"
                                                value={freeTextName}
                                                onChange={(e) => setFreeTextName(e.target.value)}
                                                fullWidth
                                            />
                                            <Grid2 container spacing={1}>
                                                <Grid2 size={6}>
    <TextField
        label="Preis (Netto)"
        value={freeTextPrice}
        onChange={(e) => setFreeTextPrice(e.target.value)}
        fullWidth
        margin="dense"
        size="small"
        type="text"
        inputMode="decimal"
        inputProps={{ inputMode: "decimal", pattern: "[0-9]*[.,]?[0-9]*" }}
    />
                                                </Grid2>
                                                <Grid2 size={6}>
                                                    <TextField
                                                        label="USt (%)"
                                                        type="text"
                                                        value={freeTextTaxRate}
                                                        onChange={(e) => setFreeTextTaxRate(e.target.value)}
                                                        inputMode="decimal"
                                                        inputProps={{ inputMode: "decimal", pattern: "[0-9]*[.,]?[0-9]*" }}
                                                        fullWidth
                                                    />
                                                </Grid2>
                                            </Grid2>
                                            <TextField
                                                label="Einheit"
                                                placeholder="Stk, h, m², ..."
                                                value={freeTextUnit}
                                                onChange={(e) => setFreeTextUnit(e.target.value)}
                                                fullWidth
                                            />
                                        </Stack>
                                    )}

                                    {(() => {
                                        const parsed = parsePositiveDecimalInput(selectedAmount);
                                        const amountErrorText = parsed.error === "invalid"
                                            ? "Bitte eine gueltige Zahl eingeben (z.B. 1,5)."
                                            : parsed.error === "too_small"
                                                ? "Menge muss mindestens 0,01 sein."
                                                : "";

                                        const isAmountValid = Boolean(parsed.value) && !parsed.error;

                                        const isAddDisabled = isFreeText
                                            ? (!freeTextName || freeTextPrice === "" || !isAmountValid)
                                            : (!selectedArticleId || !isAmountValid);

                                        return (
                                            <>
                                                <TextField
                                                    label="Anzahl"
                                                    value={selectedAmount}
                                                    onChange={(e) => setSelectedAmount(e.target.value)}
                                                    fullWidth
                                                    margin="dense"
                                                    size="small"
                                                    // Use text + decimal input mode so users can enter "1,5" as well as "1.5".
                                                    type="text"
                                                    inputMode="decimal"
                                                    inputProps={{ inputMode: "decimal", pattern: "[0-9]*[.,]?[0-9]*" }}
                                                    error={Boolean(amountErrorText)}
                                                    helperText={amountErrorText}
                                                />

                                    {/* Rabatt je Artikel */}
                                    <FormControl component="fieldset">
                                        <FormLabel component="legend">Rabatt auf Artikel?</FormLabel>
                                        <RadioGroup
                                            row
                                            value={useArticleDiscount ? "yes" : "no"}
                                            onChange={(e) => setArticleDiscount(e.target.value === "yes")}
                                        >
                                            <FormControlLabel value="yes" control={<Radio />} label="Ja" />
                                            <FormControlLabel value="no" control={<Radio />} label="Nein" />
                                        </RadioGroup>
                                    </FormControl>

                                    {useArticleDiscount && (
                                        <>
                                            <TextField
                                                type="number"
                                                label="Artikelrabatt (%)"
                                                value={articleDiscountPercent}
                                                onChange={(e) => setArticleDiscountPercent(e.target.value)}
                                                inputMode="decimal"
                                                inputProps={{ min: "0", max: "100", step: "0.01" }}
                                                fullWidth
                                            />
                                            <TextField
                                                label="Grund für Artikelrabatt"
                                                value={articleDiscountReason}
                                                onChange={(e) => setArticleDiscountReason(e.target.value)}
                                                fullWidth
                                            />
                                        </>
                                    )}

	                                    <Button
	                                        variant="contained"
	                                        onClick={handleAddArticle}
	                                        sx={{ bgcolor: "orange", "&:hover": { bgcolor: "#cc7a00" }, borderRadius: 2, color: "#000" }}
	                                        disabled={isAddDisabled}
	                                    >
	                                        Artikel hinzufügen
	                                    </Button>
                                            </>
                                        );
                                    })()}

                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={hasShippingCost}
                                                onChange={(e) => handleToggleShippingCost(e.target.checked)}
                                                disabled={sortedShippingCosts.length === 0}
                                            />
                                        }
                                        label="Versandkosten hinzufügen"
                                    />

                                    <TextField
                                        select
                                        label="Versandkosten auswählen"
                                        value={selectedShippingCostId}
                                        onChange={(e) => handleShippingCostSelectionChange(e.target.value)}
                                        fullWidth
                                    >
                                        {sortedShippingCosts.length === 0 ? (
                                            <MenuItem value="" disabled>
                                                Keine Versandkosten vorhanden
                                            </MenuItem>
                                        ) : (
                                            <MenuItem value="">Keine Versandkosten</MenuItem>
                                        )}
                                        {sortedShippingCosts.map((cost) => {
                                            const net = cost.price ?? 0;
                                            const gross = net * (1 + (cost.taxRate || 0) / 100);
                                            const label = profil?.kleinunternehmer
                                                ? `${cost.name} - ${net.toFixed(2)} €`
                                                : `${cost.name} - ${net.toFixed(2)} € netto (${gross.toFixed(2)} € brutto)`;
                                            return (
                                                <MenuItem key={cost.id} value={cost.id}>
                                                    {label}
                                                </MenuItem>
                                            );
                                        })}
                                    </TextField>

                                    <Divider sx={{ my: 1 }} />

                                    {/* Gesamtrabatt */}
                                    <FormControl component="fieldset" error={Boolean(fieldErrors.totalDiscountType)}>
                                        <FormLabel component="legend">Gesamtrabatt</FormLabel>
                                        <RadioGroup
                                            row
                                            value={totalDiscountType}
                                            onChange={(e) => {
                                                setTotalDiscountType(e.target.value);
                                                clearFieldError("totalDiscountType");
                                            }}
                                        >
                                            <FormControlLabel value="none" control={<Radio />} label="Kein Rabatt" />
                                            <FormControlLabel value="percent" control={<Radio />} label="Prozentual" />
                                            <FormControlLabel value="fixed" control={<Radio />} label="Fester Betrag" />
                                        </RadioGroup>
                                        {fieldErrors.totalDiscountType && (
                                            <FormHelperText>{fieldErrors.totalDiscountType}</FormHelperText>
                                        )}
                                    </FormControl>

                                    {totalDiscountType !== "none" && (
                                        <TextField
                                            type="number"
                                            label={totalDiscountType === "percent" ? "Rabatt in %" : "Rabatt in €"}
                                            value={totalDiscountValue}
                                            onChange={(e) => {
                                                setTotalDiscountValue(e.target.value);
                                                clearFieldError("totalDiscountValue");
                                            }}
                                            inputMode="decimal"
                                            inputProps={{ min: "0", step: "0.01" }}
                                            fullWidth
                                            error={Boolean(fieldErrors.totalDiscountValue)}
                                            helperText={fieldErrors.totalDiscountValue || ""}
                                        />
                                    )}

                                    <Divider sx={{ my: 1 }} />

                                    <MultilineTextField
                                        label="Freitext (nur PDF)"
                                        value={invoiceFreeText}
                                        onChange={(e) => setInvoiceFreeText(e.target.value)}
                                        minRows={3}
                                        maxRows={6}
                                        helperText="Wird nur auf der PDF-Rechnung angezeigt."
                                        fullWidth
                                    />

                                </CardContent>
                            </Card>
                        </Grid2>

                        {/* Vorschau rechts */}
                        <Grid2 xs={12} md={8} sx={{ minHeight: 0 }}>
                            <Card sx={{ borderRadius: 3, boxShadow: "0 6px 24px rgba(0,0,0,0.06)" }}>
                                <CardContent>
                                    {/* Header / Kunde */}
                                    <Grid2 container justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                                        <Grid2>
                                            <Typography variant="h6" fontWeight={700}>
                                                Vorschau
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {documentNumberLabel}: {invoiceNextNumber || "—"}
                                            </Typography>
                                        </Grid2>
                                        <Grid2>
                                            {logoUrl && (
                                                <img
                                                    src={logoUrl}
                                                    alt="Logo"
                                                    style={{ maxHeight: 56, maxWidth: 160, objectFit: "contain" }}
                                                />
                                            )}
                                        </Grid2>
                                    </Grid2>

                                    <Divider sx={{ my: 1.5 }} />

                                    {selectedCustomer && (
                                        <Box sx={{ mb: 1.5 }}>
                                            <Typography variant="body1" fontWeight={600}>
                                                {selectedCustomer.name} {selectedCustomer.lastname}
                                            </Typography>
                                            {(selectedCustomer.addresses?.length ?? 0) > 0 && (
                                                <Typography variant="body2" color="text.secondary">
                                                    {selectedCustomer.addresses[0].street},{" "}
                                                    {selectedCustomer.addresses[0].zip} {selectedCustomer.addresses[0].city}
                                                </Typography>
                                            )}

                                            {selectedLocation && typeof selectedLocation === "object" && (
                                                <Typography variant="body2" color="text.secondary">
                                                    Leistungsort: {selectedLocation.street}, {selectedLocation.zip} {selectedLocation.city}
                                                </Typography>
                                            )}
                                            {selectedWorkOrder && (
                                                <Typography variant="body2" color="text.secondary">
                                                    Auftrag: {selectedWorkOrder.title || selectedWorkOrder.id?.slice(0, 8)}
                                                </Typography>
                                            )}
                                        </Box>
                                    )}

                                    <Grid2 container spacing={2} sx={{ mb: 1 }}>
                                        <Grid2 xs={12} sm={6}>
                                            <Typography variant="body2">
                                                <strong>{documentDateLabel}:</strong> {invoiceDate}
                                            </Typography>
                                        </Grid2>
                                        <Grid2 xs={12} sm={6}>
                                            <Typography variant="body2">
                                                <strong>{documentDueLabel}:</strong> {dueDate}
                                            </Typography>
                                        </Grid2>
                                        <Grid2 xs={12} sm={6}>
                                            <Typography variant="body2">
                                                <strong>Leistungsdatum:</strong> {performanceDate}
                                            </Typography>
                                        </Grid2>
                                    </Grid2>

                                    <Divider sx={{ my: 1.5 }} />

                                    {/* Artikelzeilen */}
                                    <Typography variant="subtitle1" fontWeight={700}>
                                        Positionen
                                    </Typography>

                                    <List dense>
                                        {invoiceArticles.map((a, i) => {
                                            const base = (a.price || 0) * (a.amount || 0);
                                            const net = a.articleDiscount ? base * (1 - (a.articleDiscount || 0) / 100) : base;
                                            const showVat = !profil?.kleinunternehmer;
                                            const lineTotal = showVat ? net * (1 + (a.taxRate || 0) / 100) : net;
                                            const suffix = showVat ? ` (${a.taxRate || 0}% USt)` : "";

                                            const secondary = a.articleDiscount
                                                ? `${fmtEUR(lineTotal)}${suffix && ` –${suffix}`} ${
                                                    a.articleDiscountReason ? `– ${a.articleDiscountReason}` : ""
                                                }`
                                                : `${fmtEUR(lineTotal)}${suffix}`;
                                            const lineErrorSummary = getArticleErrorSummary(i);

                                            return (
                                                <ListItem
                                                    key={i}
                                                    secondaryAction={
                                                        <IconButton edge="end" onClick={() => handleRemoveInvoiceArticle(i)} aria-label="Löschen">
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    }
                                                >
                                                    <ListItemText
                                                        primary={`${a.amount}× ${a.name}${a.articleDiscount ? ` (−${a.articleDiscount}%)` : ""}`}
                                                        secondary={
                                                            lineErrorSummary ? (
                                                                <>
                                                                    <span>{secondary}</span>
                                                                    <Typography variant="caption" color="error" display="block">
                                                                        {lineErrorSummary}
                                                                    </Typography>
                                                                </>
                                                            ) : (
                                                                secondary
                                                            )
                                                        }
                                                    />
                                                </ListItem>
                                            );
                                        })}
                                        {invoiceArticles.length === 0 && (
                                            <ListItem>
                                                <ListItemText
                                                    primaryTypographyProps={{ color: "text.secondary" }}
                                                    primary="Noch keine Positionen hinzugefügt."
                                                />
                                            </ListItem>
                                        )}
                                    </List>
                                    {fieldErrors.articles && (
                                        <FormHelperText error>{fieldErrors.articles}</FormHelperText>
                                    )}

                                    {invoiceFreeText.trim() && (
                                        <Box sx={{ mb: 1.5 }}>
                                            <Typography variant="subtitle2" fontWeight={600}>
                                                Freitext
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-wrap" }}>
                                                {invoiceFreeText}
                                            </Typography>
                                        </Box>
                                    )}

                                    <Divider sx={{ my: 1.5 }} />

                                    {/* Summen */}
                                    {profil?.kleinunternehmer ? (
                                        <>
                                            {totalDiscountType !== "none" && (
                                                <Typography variant="body2" color="text.secondary">
                                                    Rabatt: {fmtEUR(totals.discountAmount)}
                                                </Typography>
                                            )}
                                            <Typography variant="h6" sx={{ mt: 1 }}>
                                                Gesamtbetrag: {fmtEUR(totals.totalGross)}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                                Hinweis: Umsatzsteuer gemäß § 19 UStG nicht ausgewiesen (Kleinunternehmerregelung).
                                            </Typography>
                                        </>
                                    ) : (
                                        <>
                                            <Typography variant="body2">Netto: {fmtEUR(totals.totalNet)}</Typography>
                                            {Object.entries(totals.vatByRate).map(([rate, amount]) => (
                                                <Typography key={rate} variant="body2">
                                                    Umsatzsteuer ({rate}%): {fmtEUR(amount)}
                                                </Typography>
                                            ))}
                                            {totalDiscountType !== "none" && (
                                                <Typography variant="body2" color="text.secondary">
                                                    Rabatt: {fmtEUR(totals.discountAmount)}
                                                </Typography>
                                            )}
                                            <Typography variant="h6" sx={{ mt: 1 }}>
                                                Gesamt (Brutto): {fmtEUR(totals.totalGross)}
                                            </Typography>
                                        </>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid2>
                    </Grid2>
                </DialogContent>

                <DialogActions>
                    <Button onClick={handleCloseCreateDialog} color="inherit">
                        Abbrechen
                    </Button>
                    <Button
                        onClick={handlePreviewInvoice}
                        variant="outlined"
                        sx={{ borderRadius: 2 }}
                        disabled={!selectedCustomerId || invoiceArticles.length === 0}
                    >
                        Vorschau
                    </Button>
                    <Button
                        onClick={handleCreateInvoice}
                        variant="contained"
                        sx={{ bgcolor: "orange", "&:hover": { bgcolor: "#cc7a00" }, borderRadius: 2, color: "#000" }}
                        disabled={!selectedCustomerId || invoiceArticles.length === 0}
                    >
                        {documentLabel} erstellen
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Auftragszuordnung */}
            <Dialog
                open={workOrderDialogOpen}
                onClose={handleCloseWorkOrderDialog}
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle>Auftrag zuordnen</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={1.5}>
                        {workOrderInvoice && (
                            <>
                                <Typography variant="body2" color="text.secondary">
                                    Rechnung: {workOrderInvoice.invoiceNumber || workOrderInvoice.id?.slice(0, 8)}
                                </Typography>
                                {resolveInvoiceCustomerName(workOrderInvoice) && (
                                    <Typography variant="body2" color="text.secondary">
                                        Kunde: {resolveInvoiceCustomerName(workOrderInvoice)}
                                    </Typography>
                                )}
                                {typeof workOrderInvoice.totalGross === "number" && (
                                    <Typography variant="body2">
                                        Betrag: {fmtEUR(workOrderInvoice.totalGross)}
                                    </Typography>
                                )}
                            </>
                        )}
                        <TextField
                            select
                            label="Auftrag"
                            value={workOrderSelection}
                            onChange={(e) => setWorkOrderSelection(e.target.value)}
                            fullWidth
                        >
                            <MenuItem value="">Kein Auftrag</MenuItem>
                            {workOrderInvoice && workOrderOptionsForInvoice.length === 0 && (
                                <MenuItem value="" disabled>
                                    Keine Aufträge vorhanden
                                </MenuItem>
                            )}
                            {workOrderOptionsForInvoice.map((order) => (
                                <MenuItem key={order.id} value={order.id}>
                                    {order.title || order.id?.slice(0, 8)}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseWorkOrderDialog} color="inherit">
                        Abbrechen
                    </Button>
                    <Button
                        onClick={handleConfirmWorkOrder}
                        variant="contained"
                        sx={{ bgcolor: "orange", "&:hover": { bgcolor: "#cc7a00" }, color: "#000" }}
                        disabled={!workOrderInvoice}
                    >
                        Speichern
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={cancelDialogOpen}
                onClose={handleCloseCancelDialog}
                aria-labelledby="cancel-invoice-dialog-title"
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle id="cancel-invoice-dialog-title">Rechnung stornieren</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={1.5}>
                        {cancelInvoiceTarget && (
                            <>
                                <Typography variant="body2" color="text.secondary">
                                    Rechnung: {cancelInvoiceTarget.invoiceNumber || cancelInvoiceTarget.id?.slice(0, 8)}
                                </Typography>
                                {resolveInvoiceCustomerName(cancelInvoiceTarget) && (
                                    <Typography variant="body2" color="text.secondary">
                                        Kunde: {resolveInvoiceCustomerName(cancelInvoiceTarget)}
                                    </Typography>
                                )}
                                {typeof cancelInvoiceTarget.totalGross === "number" && (
                                    <Typography variant="body2">
                                        Betrag: {fmtEUR(cancelInvoiceTarget.totalGross)}
                                    </Typography>
                                )}
                            </>
                        )}
                        <Typography>
                            Diese Rechnung wirklich stornieren? Es wird eine Storno-Rechnung erstellt.
                        </Typography>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseCancelDialog} color="inherit" disabled={cancelPending}>
                        Abbrechen
                    </Button>
                    <Button
                        onClick={handleConfirmCancelInvoice}
                        variant="contained"
                        color="error"
                        disabled={!cancelInvoiceTarget || cancelPending}
                    >
                        Storno erstellen
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Zahlungsdialog */}
            <Dialog open={paymentDialogOpen} onClose={handleCancelPayment} PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle>Zahlungsdatum</DialogTitle>
                <DialogContent>
                    <AppDatePicker
                        label="Zahlungsdatum"
                        value={paymentDate}
                        onChange={(value) => setPaymentDate(value ?? "")}
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelPayment} color="inherit">
                        Abbrechen
                    </Button>
                    <Button
                        onClick={handleConfirmPayment}
                        variant="contained"
                        sx={{ bgcolor: "orange", "&:hover": { bgcolor: "#cc7a00" }, color: "#000" }}
                    >
                        Speichern
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Teilzahlungsdialog */}
            <Dialog
                open={partialPaymentDialogOpen}
                onClose={handleCancelPartialPayment}
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle>Teilzahlung erfassen</DialogTitle>
                <DialogContent>
                    <Stack spacing={1.5} sx={{ mt: 1 }}>
                        {partialPaymentInvoice && (
                            <>
                                <Typography variant="body2" color="text.secondary">
                                    Rechnung: {partialPaymentInvoice.invoiceNumber || partialPaymentInvoice.id?.slice(0, 8)}
                                </Typography>
                                {partialPaymentInvoice.totalGross != null && (
                                    <Typography variant="body2">
                                        Gesamtbetrag: {fmtEUR(toNumberSafe(partialPaymentInvoice.totalGross))}
                                    </Typography>
                                )}
                                <Typography variant="body2" color="text.secondary">
                                    Bereits bezahlt: {fmtEUR(getInvoicePaidAmount(partialPaymentInvoice))}
                                </Typography>
                                {getInvoiceOutstandingAmount(partialPaymentInvoice) !== null && (
                                    <Typography variant="body2" color="text.secondary">
                                        noch offen: {fmtEUR(getInvoiceOutstandingAmount(partialPaymentInvoice))}
                                    </Typography>
                                )}
                            </>
                        )}
                        <TextField
                            label={`Teilzahlung (${resolvePaymentCurrency(partialPaymentInvoice)})`}
                            type="text"
                            value={partialPaymentAmount}
                            onChange={(e) => setPartialPaymentAmount(e.target.value)}
                            inputMode="decimal"
                            inputProps={{ inputMode: "decimal", pattern: "[0-9]*[.,]?[0-9]*" }}
                            fullWidth
                        />
                        <AppDatePicker
                            label="Zahlungsdatum"
                            value={partialPaymentDate}
                            onChange={(value) => setPartialPaymentDate(value ?? "")}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelPartialPayment} color="inherit">
                        Abbrechen
                    </Button>
                    <Button
                        onClick={handleConfirmPartialPayment}
                        variant="contained"
                        sx={{ bgcolor: "orange", "&:hover": { bgcolor: "#cc7a00" }, color: "#000" }}
                    >
                        Speichern
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snack.open}
                autoHideDuration={3500}
                onClose={() => setSnack((s) => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert onClose={() => setSnack((s) => ({ ...s, open: false }))} severity={snack.sev} sx={{ width: "100%" }}>
                    {snack.msg}
                </Alert>
            </Snackbar>
        </>
    );
}
